// ProductsView — Product definition (definition-only).
//
// A47 PROOF: TreeNav and DataGrid imported WITHOUT modification.
// BUILD 2: live client-side resolver. P1: the placement workspace moved to the
//           Project screen — this form is now definition-only (typical config +
//           components + Where-used).
// 2026-08-02: the right-hand "Configuration — Definition" ParameterPanel is GONE.
//           Typical is edited in the parameters grid (and saves); the new SIMULATE
//           column is scratch-only and drives the live preview without ever writing.
//           ParameterPanel.jsx now has no consumer anywhere.

import { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import { formatMoney, formatQty } from './formatMoney.js';
import { rate } from './format.js';
import MoneyCell from './MoneyCell.jsx';
import HourCell from './HourCell.jsx';
import Text from './components/Text.jsx';

import DataGrid from './components/DataGrid.jsx';
import AssistantSection from './components/AssistantSection.jsx';
// The Function screen's parameter line, reused verbatim — see its export comment.
import { ParamLine, formatParamValue } from './components/PlacementConfigSubRow.jsx';
import Notice from './components/Notice.jsx';
import AssistantWriteNotice, { useAssistantWrite } from './components/AssistantWriteNotice.jsx';
import { makeFolderCols } from './folder-cols.jsx';
import { useAuthGroup } from './AuthGate.jsx';
import { useCanChange } from './can-change.js';
import TextCell from './components/TextCell.jsx';
import { CELL_FONT } from './components/cellStyles.js';
import SelectCell from './components/SelectCell.jsx';
import CheckboxCell from './components/CheckboxCell.jsx';
import FormulaCell from './components/FormulaCell.jsx';
import CascadeSelect, { CascadeDropdown, useCascadeSignal } from './components/CascadeSelect.jsx';
import OnshapeSyncReview from './components/OnshapeSyncReview.jsx';
import SyncWorklistSection from './components/SyncWorklistSection.jsx';
import SyncNotApplicableSection from './components/SyncNotApplicableSection.jsx';
import OnshapeSection from './components/OnshapeSection.jsx';
import DbIdBadge from './components/DbIdBadge.jsx';
import Breadcrumb from './components/Breadcrumb.jsx';
import { composeTree, ownOnlyTree } from './tree-model.js';
import ShowStandardCheck, { nothingOwnMessage } from './components/ShowStandardCheck.jsx';
import { WorkingInContext } from './can-change.js';
import HeaderRow from './components/HeaderRow.jsx';
import Card from './components/Card.jsx';
import NotesSection from './components/NotesSection.jsx';
import CategoryOnshapePanel from './components/CategoryOnshapePanel.jsx';
import SectionTitle from './components/SectionTitle.jsx';
import HierarchyWorkspace from './components/HierarchyWorkspace.jsx';
import ImageCell from './components/ImageCell.jsx';
import ViewSwitcher from './components/ViewSwitcher.jsx';
import { buildRefDisplay, resolveComps, evalFormula } from './product-resolver.js';
import { buildParamEnv } from '../param-env.js';
import { makeParamCols, makeProductCols, makeGridCols, resolveListOptions, componentsCountLabel } from './product-cols.jsx';

import { NAVY, PANE_GAP, BTN_PRIMARY, BTN_SECONDARY, BTN_DISABLED, BTN_GLYPH } from './theme-tokens.js';
import { CATALOG, PRODUCT, WARN, CRUMB_SEP, OnshapeMark } from './icons.js';
import { USED_IN_PRODUCTS_COLS, PLACED_IN_PROJECTS_COLS } from './usage-cols.jsx';

// relativeTimeAgo and ConfigCheckReport removed 2026-07-17 with the "Sync from
// Onshape" massprops button — their only callers. See the removal note at the
// product Onshape card.

// Phase 1c note — OnshapeModelViewer moved into the shared
// components/OnshapeSection.jsx (UI_VISION §8 step 1) with the section that
// mounts it. Since 2026-10-15 it renders components/Model3D.jsx (three.js, pinned),
// which replaced the <model-viewer> CDN tag.

// ---------------------------------------------------------------------------
// usageCardContent — the body of EITHER "Where used" card ("Used in Products" /
// "Placed in Projects"). Both read from the SAME usage fetch (one call, WU1/WU2's
// original reasoning still holds: the two facts come from one query), but as of
// 2026-08-20 render as two INDEPENDENT cards (Alex: "totally independent one from
// the other... two cards instead... almost behave the same") rather than one
// section with two nested sub-collapses — same card shell every other spot on
// this screen already uses, chevron included, no second collapse mechanism.
//
// CLOSED BY DEFAULT, LOADED ON OPEN (Alex, 2026-08-20: "load the query when the
// user opens the card — closed by default," replacing the explicit "Load
// where-used" button this used to render). Both cards default `defaultOpen:
// false` and set `onOpen: loadUsage` on their spot — opening EITHER one loads
// the shared fetch, and the other one's count/rows appear the moment it
// resolves. Content is only ever mounted while open (Spot's `{open &&
// spot.content}`), so the ONLY states reachable here are "still loading" and
// "loaded" — there is no button left to click, so no third state to show.
// Zero references → "Not used anywhere" (signal the product is safe to delete),
// shown on the card whose own list is empty — each card only speaks for itself.
// ---------------------------------------------------------------------------
function usageCardContent(usage, rows, columns, onRowClick, emptyMessage, storageKey) {
  if (usage == null) {
    return <Text kind="note" as="div">Loading usage…</Text>;
  }
  return (
    <DataGrid
      rows={rows}
      columns={columns}
      onRowClick={onRowClick}
      emptyMessage={emptyMessage}
      storageKey={storageKey}
    />
  );
}

// ---------------------------------------------------------------------------
// Component editor — the SINGLE inline component editor, for BOTH add and edit
// (WS1a-iii, CO9). A blank comp (no `id`) drives Add; an existing comp drives
// Edit. The state/logic lives in the useCompEditor hook (below) and the controls
// are the shared editor PIECES (DescriptionEditor/SourceEditor/ComponentPickEditor/
// SizeEditor/QtyEditor); S3e renders them IN-CELL in ComponentsGrid for both add
// (blank ghost row) and edit (selected row). Definition mode only.
//
// Identity rule (v28, Alex 2026-07-02 — "write-identical" retired):
//   • Add AND Edit both show the SAME interactive pickers — Source select, the
//     L1→L2→L3 cascade + Item + unified Size for Catalog, or the L1→L2→L3→Product
//     cascade for Product. Edit pre-seeds the cascade from the comp's current path
//     (sized → comp._family_l1 + sized_lines[0].l2/l3; plain line → comp.l1/l2/l3;
//     unknown → filters start empty) so re-picking is optional, not mandatory.
//   Name stays read-only, auto-filled from the pick either way.
// Row 2's Qty is the single quantity input (qty_expr). The retired legacy
// inputs (base_qty/driver_param/unit_or_ratio/factor, WS0.1) are gone; the
// server keeps its defaults for add and preserves existing values on edit.
//
// L1/L2/L3 filter state is now INTERNAL (moved from ProductsView props, v28) —
// each form instance is self-contained; the text-search wiring (searchQuery/
// searchResults/onCompSearch) still arrives via props from ProductsView.
// categoryTree (PRODUCTS tree, for the Product-source cascade) also arrives via
// props from ProductsView (already fetched there for the hierarchy grid).
// ---------------------------------------------------------------------------

// Bug-fix (2026-07-09): walk the PRODUCTS tree (categoryTree — c1-/c2-/c3-
// nodes, product leaves flagged isProduct with _productId) to find the L1/L2/L3
// ancestor codes for a given product id. Used to back-fill the Product-source
// cascade (prodFilters) when editing an existing kind='product' comp, whose
// ref_id is that sub-product's id but carries no l1/l2/l3 of its own.
function findProdCategoryPath(nodes, productId, path = []) {
  for (const n of nodes) {
    if (n.isProduct) {
      if (n._productId === productId) return path;
      continue;
    }
    if (n.children) {
      const found = findProdCategoryPath(n.children, productId, [...path, n]);
      if (found) return found;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// S3d — seed helpers + the useCompEditor hook. The ENTIRE editing state/logic for
// one component (add OR edit) lives here so BOTH consumers share ONE implementation
// (the wiring is unchanged — only WHERE the editors render differs per consumer):
//   • ComponentsGrid ADD  — S3e: the SAME editors rendered IN a blank/ghost row's own
//     cells (DataGrid addInlineEditRender) + a parameters/Add-Cancel extension row
//     (renderAddSubRow), opened from the "+ Add component" ghost row.
//   • ComponentsGrid EDIT — the SAME editors rendered IN the selected row's own cells
//     (DataGrid inlineEditRowId + per-column editRender) + a parameters extension
//     row (renderSubRow) — so the fields ARE the grid cells and track column-resize.
// The seed helpers are pure functions of `comp` (used for BOTH useState init AND the
// in-render reset), so a persistent EDIT instance reseeds byte-identically to a fresh
// ADD mount when the edited comp changes.
// ---------------------------------------------------------------------------
function seedCompFilters(comp) {
  if (comp.id == null) return { l1: '', l2: '', l3: '' };
  if (comp.size_mode === 'sized' && (comp.sized_lines || []).length > 0) {
    const sl = comp.sized_lines[0];
    return { l1: comp._family_l1 || sl.l1 || '', l2: sl.l2 || '', l3: sl.l3 || '' };
  }
  return { l1: comp.l1 || '', l2: comp.l2 || '', l3: comp.l3 || '' };
}
function seedProdFilters(comp, categoryTree) {
  if (comp.id == null || comp.kind !== 'product' || !comp.ref_id) return { l1: '', l2: '', l3: '' };
  const path = findProdCategoryPath(categoryTree, Number(comp.ref_id));
  if (!path) return { l1: '', l2: '', l3: '' };
  return { l1: path[0]?.code || '', l2: path[1]?.code || '', l3: path[2]?.code || '' };
}
function seedParamMap(comp) {
  if (!comp.param_map) return {};
  try { const p = JSON.parse(comp.param_map); return (p && typeof p === 'object') ? p : {}; } catch { return {}; }
}
function seedFields(comp) {
  return {
    kind: comp.kind || 'line',
    ref_id: comp.ref_id || '',
    ref_label: comp.ref_label || '',
    label: comp.label || '',
    description: comp.description || '',
    size_mode: comp.size_mode || 'NA',
    size_expr: comp.size_expr || '',
    qty_expr: comp.qty_expr || '',
    param_map: seedParamMap(comp),
  };
}
function seedSubParams(comp) {
  return (comp._sub_param_names && comp.ref_id) ? { [comp.ref_id]: comp._sub_param_names } : {};
}

function useCompEditor({
  comp, params, categoryTree = [], onSave,
  searchQuery, searchResults, onCompSearch,
  catalogTaxonomy = [], localValues, product,
  sizeListValuesCache, fetchSizeListValues,
}) {
  const isAdd = comp.id == null;

  // Seed the catalog cascade filters from the comp's CURRENT path (edit mode) so
  // re-picking is optional: sized refs use _family_l1 + sized_lines[0].l2/l3;
  // plain line refs use comp.l1/l2/l3; unknown → empty (Add always starts empty).
  const [searchFilters, setSearchFilters] = useState(() => seedCompFilters(comp));
  const onCompFilterChange = (level, value) => {
    setSearchFilters(prev => {
      const next = level === 'l1' ? { l1: value, l2: '', l3: '' }
        : level === 'l2' ? { ...prev, l2: value, l3: '' }
        : { ...prev, l3: value };
      onCompSearch(searchQuery, undefined, next);
      return next;
    });
  };

  // Products-tree filters (Source=Product cascade, v28 item 2a) — same L1→L2→L3
  // shape but walking categoryTree (PRODUCTS tree), independent of the catalog
  // filters above. Seeded from the comp's CURRENT product ref (edit mode).
  const [prodFilters, setProdFilters] = useState(() => seedProdFilters(comp, categoryTree));
  const onProdFilterChange = (level, value) => {
    setProdFilters(prev => level === 'l1' ? { l1: value, l2: '', l3: '' }
      : level === 'l2' ? { ...prev, l2: value, l3: '' }
      : { ...prev, l3: value });
  };

  // S3g — guided auto-open flow. A monotonic { key, n } tick: `openNext(key)` bumps `n`
  // and tags the control to open; each cascade control is handed `sig(key)` as its
  // CascadeDropdown `openSignal`, so only the matching control opens (once per bump).
  // `advance(step)` maps a just-picked step to the next control and opens it, so a pick
  // flows Source→L1→L2→L3→item→Size without re-clicking. `n` never resets (stays
  // monotonic so a persistent dropdown's last-handled ref can't swallow a later open);
  // on comp reseed only the KEY is cleared, so switching rows never auto-opens anything.
  // 2026-08-04: this WAS the reference implementation of the convention; it now comes
  // from useCascadeSignal so there is exactly one copy in the codebase.
  const _cascade = useCascadeSignal();
  const openNext = _cascade.advance;
  const sig = _cascade.signalFor;

  // Q8 v2 — param_map: { subParamName: 'formula over PARENT params' }. Only linked
  // (Source='⇐ link') rows appear here; a row missing from this object is '⚑ typical'.
  const [fields, setFields] = useState(() => seedFields(comp));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setFields(f => ({ ...f, [k]: v }));
  const setParamMapRow = (subParamName, formula) => setFields(f => ({ ...f, param_map: { ...f.param_map, [subParamName]: formula } }));
  const unsetParamMapRow = (subParamName) => setFields(f => {
    const next = { ...f.param_map };
    delete next[subParamName];
    return { ...f, param_map: next };
  });

  // Q8 v2 — mapping table: the selected sub-product's FULL param list (all kinds —
  // Mockup A shows one row per sub-product param), fetched ONE time when a Product
  // ref is (re-)picked (not on every render). Cached per ref_id in this component's
  // own state so re-opening the same pick doesn't refetch. Also embedded server-side
  // as comp._sub_param_names when the comp already has a param_map (edit mode) — used
  // as an immediate seed so the table renders before the fetch resolves.
  const [subParamsByRef, setSubParamsByRef] = useState(() => seedSubParams(comp));
  // Which param a sub-product's Qty drives, per ref. On EDIT the server already embeds
  // it (comp._sub_qty_param); on ADD there is no comp yet, so it is read from the same
  // /api/products/:id fetch below -- getProduct started returning qty_param on
  // 2026-08-04. Without this the filter would work when editing and silently not when
  // adding, which is the worse of the two failures.
  const [subQtyByRef, setSubQtyByRef] = useState(() => (comp?._sub_qty_param && comp.ref_id ? { [comp.ref_id]: comp._sub_qty_param } : {}));

  // S3d — in-render reset when the edited comp changes (see hook header). Guarded by
  // seededId so it runs exactly once per comp switch. didPrefillRef is declared here
  // (moved up from below) because the reset restores it too.
  const [seededId, setSeededId] = useState(comp.id ?? (comp._seedKey != null ? `__add__${comp._seedKey}` : '__add__'));
  const didPrefillRef = useRef(comp.ref_id || null);
  // S3e — an in-cell ADD editor is a persistent instance (it lives in ComponentsGrid, not a
  // per-open mount like the retired full-width form), so its comp.id stays null across opens
  // and the plain '__add__' key would never change → stale picks on the next open. ADD passes
  // a changing comp._seedKey (the add-open nonce) so curId changes and the reseed fires blank
  // each open. EDIT comps carry an id and never set _seedKey — byte-for-byte unchanged.
  const curId = comp.id ?? (comp._seedKey != null ? `__add__${comp._seedKey}` : '__add__');
  if (curId !== seededId) {
    setSeededId(curId);
    setFields(seedFields(comp));
    setSearchFilters(seedCompFilters(comp));
    setProdFilters(seedProdFilters(comp, categoryTree));
    setSubParamsByRef(seedSubParams(comp));
    setSaving(false);
    didPrefillRef.current = comp.ref_id || null;
    _cascade.reset(); // S3g — clear the auto-open target on comp switch, so switching rows never auto-opens anything
  }
  useEffect(() => {
    if (fields.kind !== 'product' || !fields.ref_id) return;
    const existing = subParamsByRef[fields.ref_id];
    // S3-size-unify — the server's immediate seed (comp._sub_param_names, via
    // seedSubParams) only carries {name, unit, typical_value} — enough for the
    // (pre-existing) formula mapping table, but not `kind`/`size_unit`, which the
    // Size-cell dropdown needs to detect a sub size param. Don't treat that seed as
    // "already loaded" — still fetch the full param list once to replace it.
    if (existing && existing.every(p => 'kind' in p)) return;
    let cancelled = false;
    fetch(`/api/products/${fields.ref_id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        setSubParamsByRef(prev => ({ ...prev, [fields.ref_id]: data.params || [] }));
        setSubQtyByRef(prev => ({ ...prev, [fields.ref_id]: data.qty_param || null }));
      })
      .catch(() => { /* non-fatal — mapping table just stays empty */ });
    return () => { cancelled = true; };
  }, [fields.kind, fields.ref_id, subParamsByRef]);
  // ONLY CONFIGURABLE PARAMS ARE BROADCAST (Alex, 2026-08-03: "when using it in
  // another product, the weight, scrap, and rate must not be configurable
  // parameters but internal! ... Can we add a check box for configurable yes/no
  // and based on that, not broadcast them."). This is the not-broadcasting: an
  // internal param never reaches the mapping table, so it cannot be linked, cannot
  // be left "typical", and stops being one of six rows where two were real choices.
  // The server ignores an override aimed at one anyway (param-env.js) — this is
  // what stops it being OFFERED.
  //
  // ...AND the quantity driver is not a mapping either (Alex, 2026-08-04: "In Product,
  // it filter correctly for Typical, but should also filter the one selected for
  // quantity"). On a quantity-based sub-product the driver IS the Qty cell -- it is set
  // by the component's own quantity, right there in the row. Offering it a second time
  // in the mapping table invites two answers to one question, and the Qty column wins,
  // so the mapping would silently do nothing.
  const subQtyParam = subQtyByRef[fields.ref_id] ?? comp?._sub_qty_param ?? null;
  const subMappableParams = fields.kind === 'product'
    ? (subParamsByRef[fields.ref_id] || [])
        .filter(p => p.configurable == null || Number(p.configurable))
        .filter(p => !subQtyParam || p.name !== subQtyParam)
    : [];

  // S3-size-unify — the sub-product's OWN size param(s): a standard-unit list param
  // (kind='list' with a size_lists Size Unit, not 'custom') — same "standard size
  // list" test as sizeParams' list clause below (~line 589), applied to the SUB's
  // params instead of the parent's. These get the Size cell's catalog-style
  // fixed/follow dropdown (SizeEditor) instead of sitting in the formula mapping
  // table. Usually exactly one. Fetch that size unit's option list lazily (same
  // cache/fetch the parent's own size params already use, passed down from the
  // top-level component) so the dropdown has resolved sizes+looks ready.
  const subSizeParams = subMappableParams.filter(sp => sp.type === 'list' && sp.size_unit && sp.size_unit !== 'custom');
  useEffect(() => {
    subSizeParams.forEach(sp => fetchSizeListValues(sp.size_unit));
  }, [subSizeParams, fetchSizeListValues]);

  // Match-by-name prefill (Alex: yes, incl. Size) — on a FRESH sub-product pick (not
  // on initial edit-mode load, which already has fields.param_map seeded from the
  // comp), any sub param whose name (case-insensitive) equals a parent param name
  // auto-links with a passthrough formula = the parent param's own name. Tracked so
  // it only runs once per ref pick (didPrefillRef, declared above with the reset),
  // not on every subMappableParams recompute (e.g. after the fetch resolves with
  // the SAME ref already prefilled).
  useEffect(() => {
    if (fields.kind !== 'product' || !fields.ref_id || subMappableParams.length === 0) return;
    if (didPrefillRef.current === fields.ref_id) return;
    didPrefillRef.current = fields.ref_id;
    const parentNamesLower = new Map(params.map(p => [p.name.toLowerCase(), p.name]));
    setFields(f => {
      const next = { ...f.param_map };
      let changed = false;
      for (const sp of subMappableParams) {
        // S3-size-unify — a sub size param is now the Size cell's own control (fixed/
        // follow dropdown); skip it here so a fresh pick doesn't auto-link it as a
        // formula row too.
        if (sp.type === 'list' && sp.size_unit && sp.size_unit !== 'custom') continue;
        const parentName = parentNamesLower.get(sp.name.toLowerCase());
        if (parentName && !(sp.name in next)) { next[sp.name] = parentName; changed = true; }
      }
      return changed ? { ...f, param_map: next } : f;
    });
  }, [fields.kind, fields.ref_id, subMappableParams, params]);

  const labelInputStyle = { fontSize: 'var(--fs-small)', color: 'var(--th-text-body)' };
  // S3c — paddingRight (not grid columnGap, see gridRowStyle below) gives fields a
  // visual gutter WITHOUT shifting later columns' start lines.
  const colStyle = { display: 'flex', flexDirection: 'column', gap: 1, paddingRight: 4, ...labelInputStyle };
  const roStyle = {
    fontSize: 'var(--fs-small)', color: 'var(--th-readonly-text)', padding: '2px 5px',
    border: '1px solid var(--th-border-subtle)', borderRadius: 3, background: 'var(--th-surface-disabled)',
    minWidth: 120, display: 'inline-block',
  };
  const familyDisplay = comp._family_display
    || (() => { const p = (fields.ref_id || '').split('|'); return `${p[2] || ''}${p[3] || ''}`; })();

  // Cascade — two-column code|label cascading dropdowns (L1→L2→L3), then the item
  // (a sized family or a standalone line under L3), then the unified Size control.
  // catalogTaxonomy roots are L1 nodes; children are L2, grandchildren L3; the
  // per-size data lines are the L3 children flagged isLine (each carries family_id,
  // size_value from /api/catalog/tree). Filtered by the previous pick at every level.
  const l1Node = searchFilters.l1 ? catalogTaxonomy.find(n => n.code === searchFilters.l1) : null;
  const l2Opts = l1Node ? (l1Node.children || []).filter(c => !c.isLine) : [];
  const l2Node = searchFilters.l2 ? l2Opts.find(n => n.code === searchFilters.l2) : null;
  const l3Opts = l2Node ? (l2Node.children || []).filter(c => !c.isLine) : [];
  const l3Node = searchFilters.l3 ? l3Opts.find(n => n.code === searchFilters.l3) : null;

  // Products-tree cascade (Source=Product, item 2a) — L1→L2→L3 walking categoryTree
  // (c1-/c2-/c3- category nodes + product leaves flagged isProduct, from /api/products/tree).
  const prodL1Node = prodFilters.l1 ? categoryTree.find(n => n.code === prodFilters.l1) : null;
  const prodL2Opts = prodL1Node ? (prodL1Node.children || []).filter(c => !c.isProduct) : [];
  const prodL2Node = prodFilters.l2 ? prodL2Opts.find(n => n.code === prodFilters.l2) : null;
  const prodL3Opts = prodL2Node ? (prodL2Node.children || []).filter(c => !c.isProduct) : [];
  const prodL3Node = prodFilters.l3 ? prodL3Opts.find(n => n.code === prodFilters.l3) : null;
  const prodLeaves = prodL3Node ? (prodL3Node.children || []).filter(c => c.isProduct) : [];

  // Group the L3 node's data lines into FAMILIES (one item per family_id). A family
  // is "sized" when any of its lines has a size_value. Lines with no family_id fall
  // back to a per-line pseudo-family keyed by the line id (non-sized standalone).
  // B2 (Alex, 2026-07-02): the TREE's `children` under an L3 are now grouped into
  // one leaf per family (for display) — the picker instead reads the flat
  // per-line list from `l3Node.lines` (added alongside `children`, unchanged
  // shape from before B2: family_id/size_value/price/unit/_lineId all still here).
  const itemFamilies = (() => {
    if (!l3Node) return [];
    const lines = l3Node.lines || [];
    const byFam = new Map();
    for (const ln of lines) {
      const key = ln.family_id != null ? `fam:${ln.family_id}` : `line:${ln._lineId}`;
      if (!byFam.has(key)) {
        byFam.set(key, { key, family_id: ln.family_id ?? null, code: ln.code, label: ln.label, lines: [] });
      }
      byFam.get(key).lines.push(ln);
    }
    return [...byFam.values()].map(f => {
      // THE SENTINEL IS NOT A SIZE (Alex, 2026-08-03: "if we have no choices
      // (Unique), can the dropdown list just not cascade more on the size?").
      // A size-less family carries one size_lists row with nps 0 rather than NULL,
      // so `size_value != null` alone called it SIZED — which offered a one-option
      // dropdown reading 0" and, worse, sent the pick over the wire as 'fam:<id>'.
      // That wire form MEANS sized, so the comp was stored size_id=NULL with
      // size_expr='0' and the resolver then hunted for a catalog line at NPS 0.
      // For an MF family (price NULL, priced by mass) no such line exists and the
      // component silently cost NOTHING: four products were totalling $0.00.
      // Excluding the sentinel here makes ref_id fall through to the LINE id, which
      // is the pinned form (size_id='unique', no expression) that the other 214
      // size-less comps already use. See scripts/rebind-unique-size-comps.mjs.
      const sizedLines = f.lines
        .filter(l => l.size_value != null && !isUniqueSize(l))
        .sort((a, b) => a.size_value - b.size_value);
      const isSized = sizedLines.length > 0;
      return {
        ...f,
        isSized,
        sizedLines,
        // ref_id follows the canonical rule (catalog-db): sized → 'fam:{id}',
        // non-sized standalone → the line's own id.
        ref_id: isSized && f.family_id != null ? `fam:${f.family_id}` : String(f.lines[0]._lineId),
      };
    });
  })();
  const selectedFamily = fields._family_key ? itemFamilies.find(f => f.key === fields._family_key) : null;

  // S2.1 Fix 3 — unified pick-from-list Size for an EXISTING sized comp (edit mode).
  // The unified CascadeDropdown normally renders only for a family freshly (re)picked
  // this session (selectedFamily). An existing sized comp has no _family_key, so it fell
  // back to the legacy typed control (where a fixed size meant typing the nps). Build a
  // family from the comp's server-embedded `sized_lines` so the SAME dropdown renders —
  // a fixed size becomes a pick, size_expr stays the bare <nps> (cost-identical).
  // Guarded: only when the current size_expr is REPRESENTABLE in the dropdown — a bare
  // nps (fixed size) or a bare identifier (a config param). A compound size formula keeps
  // the legacy control so its formula stays visible/editable (no silent overwrite).
  const existingSizeExpr = (fields.size_expr || '').trim();
  const existingSizeRepresentable = existingSizeExpr !== '' && (
    /^-?\d*\.?\d+$/.test(existingSizeExpr) || /^[a-zA-Z_]\w*$/.test(existingSizeExpr)
  );
  const existingSizedFamily = (!selectedFamily
    && fields.kind === 'line'
    && fields.size_mode === 'sized'
    && existingSizeRepresentable
    && Array.isArray(comp.sized_lines) && comp.sized_lines.length > 0)
    ? { key: comp.ref_id, isSized: true, sizedLines: comp.sized_lines, ref_id: comp.ref_id }
    : null;
  const sizeFamily = selectedFamily || existingSizedFamily;

  // Params that can drive a bound Size (the "Size" param row). The convention is a
  // param literally named "Size"; any magnitude param is also offerable. S2: a
  // size-unit list param (kind='list' with a size_lists Size Unit — NPS/Amps/…, not
  // 'custom') is offerable too — its typical_value is now the numeric size (nps), so
  // a bound component size resolves through the same env path as a magnitude param.
  const sizeParams = params.filter(p =>
    p.name === 'Size'
    || p.type === 'magnitude'
    || (p.type === 'list' && p.size_unit && p.size_unit !== 'custom')
  );

  const handleSave = async () => {
    setSaving(true);
    // Q8 v2 — serialize only LINKED rows into param_map; an empty map → NULL. Only
    // meaningful for kind='product' (server also rejects/ignores a stray value for
    // other kinds — belt-and-suspenders, the mapping table is hidden then).
    const trimmedParamMap = {};
    for (const [k, v] of Object.entries(fields.param_map || {})) {
      if (typeof v === 'string' && v.trim()) trimmedParamMap[k] = v.trim();
    }
    // No `label` and no `size_mode`: neither is stored any more (2026-07-29).
    // The name comes from the referenced family/product, and the size shape from
    // the reference itself — a family ref means sized (size_expr required), a
    // line ref means pinned (size_expr must be empty). The server enforces both.
    const common = {
      description: fields.description.trim() || null,
      size_expr: fields.size_mode === 'sized'
        ? (fields.size_expr.replace(/^\s*=\s*/, '').trim() || null) : null,
      qty_expr: fields.qty_expr.trim() || null,
      param_map: (fields.kind === 'product' && Object.keys(trimmedParamMap).length > 0) ? trimmedParamMap : null,
    };
    // v28: edit mode now sends kind/ref_id too (unlocked Source/Ref re-pick) —
    // if the user didn't touch them, these are simply the unchanged values
    // (harmless no-op on the server).
    const result = await onSave(isAdd ? null : comp.id, { kind: fields.kind, ref_id: fields.ref_id || null, ...common });
    setSaving(false);
    return result;
  };

  // S3d — env for the Qty live-resolved preview ("weight in line with qty"). Built
  // exactly like product-resolver.resolveComps: localValues[name] ?? typical_value.
  // Read-only — surfaces the number the qty formula already produces; no cost change.
  const env = {};
  for (const p of params) env[p.name] = (localValues?.[p.name]) ?? p.typical_value;

  // S3g — map a just-picked step → the next control to open, so a pick flows
  // Source→L1→L2→L3→item→Size without re-clicking. item→size for BOTH a catalog line
  // AND a product (S3 size-unify: a product's sub size param now has a Size dropdown
  // too). The bump is stored in openTick until the target mounts, so it survives a
  // product's async sub-param load — the Size dropdown auto-opens on mount. When there
  // is no Size dropdown (non-sized item, or a product with no size param) the 'size'
  // bump is inert. A cleared pick never calls this (callers guard on a truthy code).
  const advance = (step) => {
    const next = step === 'source' ? 'l1'
      : step === 'l1' ? 'l2'
      : step === 'l2' ? 'l3'
      : step === 'l3' ? 'item'
      : step === 'item' ? 'size'
      : null;
    if (next) openNext(next);
  };

  return {
    comp, isAdd, params, product, categoryTree, catalogTaxonomy,
    fields, set, setFields, saving, handleSave,
    sig, advance,
    searchFilters, onCompFilterChange, prodFilters, onProdFilterChange,
    searchQuery, searchResults, onCompSearch,
    subMappableParams, subSizeParams, sizeListValuesCache, setParamMapRow, unsetParamMapRow, didPrefillRef,
    l1Node, l2Opts, l2Node, l3Opts, l3Node,
    prodL1Node, prodL2Opts, prodL2Node, prodL3Opts, prodL3Node, prodLeaves,
    itemFamilies, selectedFamily, sizeFamily, sizeParams, familyDisplay,
    env, colStyle, roStyle, labelInputStyle,
  };
}

// ---------------------------------------------------------------------------
// S3d — editor PIECES. Each takes the hook's returned E and renders ONE region's
// control(s) (onChange/state preserved). S3e: BOTH consumers are ComponentsGrid's
// in-cell paths — the ADD blank/ghost row and the EDIT selected row — each rendering
// these pieces in the row's own cells. Destructuring E at the top gives every block
// the same local names its JSX already used — the bodies are moved, not rewritten.
// ---------------------------------------------------------------------------
function DescriptionEditor(E) {
  const { fields, set } = E;
  return (
    <TextCell
      value={fields.description}
      onChange={v => set('description', v)}
      placeholder="Instance note (e.g. Isolating valve)"
      title="Description"
    />
  );
}

function SourceEditor(E) {
  const { fields, setFields, advance } = E;
  return (
    <div>
      <CascadeDropdown
        ariaLabel="Source"
        allowClear={false}
        value={fields.kind}
        options={[
          { code: 'line', display: CATALOG, label: 'Catalog' },
          { code: 'product', display: PRODUCT, label: 'Product' },
        ]}
        onChange={v => { setFields(f => ({ ...f, kind: v, ref_id: '', ref_label: '', param_map: {} })); advance('source'); }}
      />
    </div>
  );
}

// ComponentRefEditor — S3f: the LEVEL (code) cascade of the pick (L1▸L2▸L3, catalog
// searchFilters or Product prodFilters). Renders in the Ref cell, immediately
// followed by ComponentPickEditor's leaf pick in the SAME cell (S3j) — the two used
// to sit in separate Ref/Component columns, which read as a two-step pick with no
// visible link between them even though they already shared one useCompEditor
// instance and auto-advance signal underneath. Returns a fragment; the consumer
// wraps it (together with ComponentPickEditor) in the Ref cell's flex container.
function ComponentRefEditor(E) {
  const {
    fields, setFields, colStyle, catalogTaxonomy, categoryTree,
    searchFilters, onCompFilterChange, prodFilters, onProdFilterChange,
    l1Node, l2Opts, l2Node, l3Opts,
    prodL1Node, prodL2Opts, prodL2Node, prodL3Opts,
    sig, advance,
  } = E;
  return (
    <>
      {/* Cascade — two-column code|label cascading dropdowns (L1→L2→L3), each
          filtered by the previous pick. Picking a level clears the item + any
          descendant filters (and keeps the search box's filters in sync). S3g:
          wrap={false} keeps the 3 levels on one compact line; each level's
          openSignal + advance() opens the NEXT control on a (non-clearing) pick. */}
      {fields.kind === 'line' && (
        <div style={{ ...colStyle }}>
          <CascadeSelect
            wrap={false}
            levels={[
              {
                ariaLabel: 'Level 1 category',
                width: 200,
                value: searchFilters.l1,
                openSignal: sig('l1'),
                options: catalogTaxonomy.map(n => ({ code: n.code, label: n.label })),
                onChange: (code) => { onCompFilterChange('l1', code); setFields(f => ({ ...f, _family_key: '', ref_id: '', ref_label: '' })); if (code) advance('l1'); },
              },
              {
                ariaLabel: 'Level 2 category',
                width: 200,
                value: searchFilters.l2,
                openSignal: sig('l2'),
                disabled: !l1Node,
                options: l2Opts.map(n => ({ code: n.code, label: n.label })),
                onChange: (code) => { onCompFilterChange('l2', code); setFields(f => ({ ...f, _family_key: '', ref_id: '', ref_label: '' })); if (code) advance('l2'); },
              },
              {
                ariaLabel: 'Level 3 category',
                width: 200,
                value: searchFilters.l3,
                openSignal: sig('l3'),
                disabled: !l2Node,
                options: l3Opts.map(n => ({ code: n.code, label: n.label })),
                onChange: (code) => { onCompFilterChange('l3', code); setFields(f => ({ ...f, _family_key: '', ref_id: '', ref_label: '' })); if (code) advance('l3'); },
              },
            ]}
          />
        </div>
      )}

      {/* Product source — L1→L2→L3 cascade over the PRODUCTS tree (item 2a). The
          Product leaf pick + name live in the Component cell (ComponentPickEditor). */}
      {fields.kind === 'product' && (
        <div style={{ ...colStyle }}>
          <CascadeSelect
            wrap={false}
            levels={[
              {
                ariaLabel: 'Level 1 category', width: 200,
                value: prodFilters.l1,
                openSignal: sig('l1'),
                options: categoryTree.map(n => ({ code: n.code, label: n.label })),
                onChange: (code) => { onProdFilterChange('l1', code); setFields(f => ({ ...f, ref_id: '', ref_label: '' })); if (code) advance('l1'); },
              },
              {
                ariaLabel: 'Level 2 category', width: 200,
                value: prodFilters.l2, disabled: !prodL1Node,
                openSignal: sig('l2'),
                options: prodL2Opts.map(n => ({ code: n.code, label: n.label })),
                onChange: (code) => { onProdFilterChange('l2', code); setFields(f => ({ ...f, ref_id: '', ref_label: '' })); if (code) advance('l2'); },
              },
              {
                ariaLabel: 'Level 3 category', width: 200,
                value: prodFilters.l3, disabled: !prodL2Node,
                openSignal: sig('l3'),
                options: prodL3Opts.map(n => ({ code: n.code, label: n.label })),
                onChange: (code) => { onProdFilterChange('l3', code); setFields(f => ({ ...f, ref_id: '', ref_label: '', param_map: {} })); if (code) advance('l3'); },
              },
            ]}
          />
        </div>
      )}
    </>
  );
}

// SearchResultsPopover — the component search-to-pick results list, escaped to a
// document.body portal (same trick CascadeDropdown already uses: "so it can never
// be clipped by a table cell's overflow:hidden or any scroll container"). Added
// 2026-08-20 when DataGrid's own table wrapper gained overflowX:auto (Alex: "the
// rectangle own the grid" / "should the scroll bar belong to the DataGrid instead" —
// yes) — that made the table's wrapper a clipping ancestor, and this list used to
// be position:absolute inside it, so results past the wrapper's bottom edge were
// silently unreachable. A REAL component on purpose, not folded into
// ComponentPickEditor: that function is invoked as a plain call (`ComponentPickEditor(ed)`,
// not JSX), so new hooks in it would attach to whatever caller's render happens to
// invoke it — unsafe. Mounted via JSX here, this owns its hooks regardless.
//
// The marker stays in NORMAL flow, exactly where the old absolute-positioned list
// sat (top:100%/left:0 of the input's own position:relative wrapper) — only its
// GEOMETRY is read; it is never painted, so the ancestor clipping the marker itself
// is irrelevant.
function SearchResultsPopover({ results, onPick }) {
  const markerRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return undefined;
    const update = () => {
      const r = marker.getBoundingClientRect();
      setPos({ left: r.left, top: r.top, minWidth: Math.max(r.width, 290) });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [results]);

  return (
    <>
      <span ref={markerRef} style={{ position: 'absolute', top: '100%', left: 0, width: 0, height: 0 }} />
      {pos && createPortal(
        <div style={{
          position: 'fixed', left: pos.left, top: pos.top, minWidth: pos.minWidth, marginTop: 2,
          background: 'var(--th-row-even)', border: '1px solid var(--th-border-subtle)', borderRadius: 3,
          zIndex: 200, maxHeight: 160, overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        }}>
          {results.map(r => (
            <div key={r.id}
              onClick={() => onPick(r)}
              style={{ padding: '4px 8px', cursor: 'pointer', fontSize: 'var(--fs-small)', borderBottom: '1px solid var(--th-divider-light)' }}
            >
              <strong style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)' }}>{buildRefDisplay(r)}</strong>
              {' '}<span style={{ color: 'var(--th-text-secondary)' }}>{r.label}</span>
              {r.price != null && <span style={{ color: 'var(--th-status-synced)', marginLeft: 6 }}>{rate(r.price, r.unit)}</span>}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}

// ComponentPickEditor — S3f: the ITEM/label pick (the catalog item CascadeDropdown +
// the unified search-to-pick field, or the Product leaf CascadeDropdown + read-only
// name). Renders in the Ref cell too now, right after ComponentRefEditor (S3j) — same
// useCompEditor E, so a level picked there filters these dropdowns exactly as before;
// only the CELL changed, from a separate 'label' column to alongside the cascade that
// feeds it. The 'label' column itself keeps its normal (non-edit) render while this
// row is edited — the currently-saved label, as a live preview of what's being
// replaced. Returns a fragment; the consumer wraps it in the Ref cell's flex
// container, after ComponentRefEditor.
function ComponentPickEditor(E) {
  const {
    fields, setFields, colStyle, roStyle,
    searchQuery, searchResults, onCompSearch, searchFilters,
    l3Node, itemFamilies, sizeParams,
    prodL3Node, prodLeaves, didPrefillRef,
    sig, advance,
  } = E;
  return (
    <>
      {/* Item — the final cascade dropdown: a sized family or a standalone line
          under the chosen L3. Selecting it sets ref_id (canonical: sized→fam:{id},
          non-sized→line id) and initialises the unified Size control. */}
      {fields.kind === 'line' && l3Node && (
        <div style={{ ...colStyle }}>
          <CascadeDropdown
            ariaLabel="Catalog item"
            width={230}
            placeholder="— pick item —"
            allowClear={false}
            openSignal={sig('item')}
            value={fields._family_key}
            options={itemFamilies.map(f => ({
              code: f.key,
              display: f.code,
              label: f.isSized ? `${f.label}  (${f.sizedLines.length} sizes)` : f.label,
            }))}
            onChange={(code) => {
              const fam = itemFamilies.find(f => f.key === code) || itemFamilies.find(f => f.code === code);
              if (!fam) return;
              setFields(f => ({
                ...f,
                _family_key: fam.key,
                ref_id: fam.ref_id,
                ref_label: fam.isSized ? `${fam.code} ${fam.label} (family)` : `${fam.code} ${fam.label}`,
                label: fam.label || '',
                size_mode: fam.isSized ? 'sized' : 'NA',
                size_expr: fam.isSized ? (sizeParams.some(p => p.name === 'Size') ? 'Size' : '') : '',
              }));
              advance('item'); // S3g — a sized item opens the Size picker; non-sized bumps harmlessly
            }}
          />
        </div>
      )}

      {/* Component — S3c: UNIFIED search-to-pick field (an alternative to the cascade
          above). Shows the picked component's name AND accepts free-text to
          search/replace it. All existing wiring preserved verbatim. */}
      {fields.kind === 'line' && (
        <div style={{ ...colStyle, position: 'relative', width: 220 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              value={fields.ref_id ? fields.label : searchQuery}
              onChange={e => {
                const v = e.target.value;
                if (fields.ref_id) setFields(f => ({ ...f, ref_id: '', ref_label: '' }));
                onCompSearch(v, undefined, searchFilters);
              }}
              placeholder={(searchFilters.l1 || searchFilters.l2 || searchFilters.l3) ? 'type to narrow…' : '≥2 chars to search…'}
              onClick={e => e.stopPropagation()}
              title="Component — type to search, or pick via the cascade above"
              style={{ ...CELL_FONT, padding: '2px 5px', border: '1px solid var(--th-border-subtle)', borderRadius: 3, width: '100%', boxSizing: 'border-box', flex: 1, minWidth: 0 }}
            />
            {fields.ref_id && (
              <button onClick={() => { setFields(f => ({ ...f, ref_id: '', ref_label: '' })); onCompSearch('', undefined, searchFilters); }}
                title="Clear pick — type to search again"
                style={{ ...BTN_GLYPH, flexShrink: 0, padding: 0, color: 'var(--th-text-disabled)' }}>✕</button>
            )}
          </div>
          {searchResults.length > 0 && !fields.ref_id && (
            <SearchResultsPopover
              results={searchResults}
              onPick={(r) => {
                // Derive sized-ness from the PICKED item's family (mirrors the cascade
                // item pick above), not the stale size_mode toggle — so a search-replace
                // can't leave a wrong size_mode/ref pairing. When the result is outside
                // the current L3 scope (not in itemFamilies), fall back to a plain,
                // non-sized line ref (always resolvable).
                const fam = itemFamilies.find(f => f.lines.some(l => String(l._lineId) === String(r.id)))
                  || (r.family_id != null ? itemFamilies.find(f => f.family_id === r.family_id) : null);
                const isSized = !!(fam && fam.isSized);
                const refId = fam ? fam.ref_id : String(r.id);
                const refLabel = isSized ? `${fam.code} ${fam.label} (family)` : buildRefDisplay(r);
                setFields(f => ({
                  ...f,
                  _family_key: fam ? fam.key : '',
                  ref_id: refId,
                  ref_label: refLabel,
                  label: r.label || '',
                  size_mode: isSized ? 'sized' : 'NA',
                  size_expr: isSized ? (sizeParams.some(p => p.name === 'Size') ? 'Size' : '') : '',
                }));
                onCompSearch(refLabel, { keepQuery: true });
              }}
            />
          )}
        </div>
      )}

      {/* Product — the leaf pick + read-only name (the L1→L2→L3 cascade lives in the
          Ref cell, ComponentRefEditor). */}
      {fields.kind === 'product' && (
        <>
          {prodL3Node && (
            <div style={{ ...colStyle }}>
              <CascadeDropdown
                ariaLabel="Product"
                width={230}
                placeholder="— pick product —"
                allowClear={false}
                openSignal={sig('item')}
                value={fields.ref_id}
                options={prodLeaves.map(p => ({
                  code: String(p._productId),
                  display: p.code,
                  label: p.label,
                }))}
                onChange={(code) => {
                  const prod = prodLeaves.find(p => String(p._productId) === code);
                  if (!prod) return;
                  didPrefillRef.current = null;
                  setFields(f => ({
                    ...f,
                    ref_id: String(prod._productId),
                    ref_label: `${prod.code} ${prod.label}`,
                    label: prod.label || '',
                    param_map: {},
                  }));
                  advance('item'); // S3g — opens the sub's Size dropdown next (once its params load), like a catalog item
                }}
              />
            </div>
          )}
          <label style={{ ...colStyle }}>
            <span style={{ ...roStyle, minWidth: 120 }} title="Set from the product pick — read-only">
              {fields.label || '—'}
            </span>
          </label>
        </>
      )}
    </>
  );
}

// ParamMappingTable — a component's sub-product parameters, drawn with the SAME
// ParamLine the Function screen uses (Alex, 2026-08-04: "updating product like
// function... simply doing the look").
//
// WHAT WENT AWAY: the per-row "⚑ typical / ⇐ link" dropdown. It made you declare a
// MODE before you could type anything, and the mode was always inferable from what you
// typed — the formula box already accepted a bare number, so "link" with `1200` and a
// fixed value were the same thing spelled two ways. Alex, restating it plainly:
// "typical is the default value. Then, or it keeps that value, or I type something
// else, or I type a formula." One box, three outcomes.
//
//   empty        -> the sub-product's own typical (the map has no entry for it)
//   a number     -> that value
//   an expression-> a formula over the PARENT's parameters
//
// A list or boolean sub-param keeps its own control (a dropdown, a checkbox): "type
// something else" means picking there, and a formula would be meaningless.
//
// `resolves to` stays. Function has no equivalent because a placement's value IS the
// number; here the cell may hold an expression, and this is the only thing that shows
// it evaluates — against the parent's typicals, with ⚠ when it does not.
function ParamMappingTable(E, ctx) {
  const { fields, params, subMappableParams, setParamMapRow, unsetParamMapRow } = E;
  // S3-size-unify — a sub size param is now the Size cell's own control (SizeEditor);
  // exclude it here so it isn't ALSO offered as a formula row.
  const formulaParams = subMappableParams.filter(sp => !(sp.type === 'list' && sp.size_unit && sp.size_unit !== 'custom'));
  if (!(fields.kind === 'product' && fields.ref_id && formulaParams.length > 0)) return null;

  const parentTypicalEnv = {};
  for (const p of params) parentTypicalEnv[p.name] = p.typical_value;

  // Line the VALUE boxes up with the grid's own value columns, not just the block's
  // left edge (Alex: "all the input data almost in line"). The name column is sized to
  // exactly the gap between the Component and Size columns, read live, so it stays
  // aligned when either is resized. Falls back to ParamLine's own default off-grid.
  const compAt = ctx?.offsetOf?.('label');
  const sizeAt = ctx?.offsetOf?.('size_display');
  const nameWidth = (compAt != null && sizeAt != null && sizeAt - compAt > 40)
    ? sizeAt - compAt - 8   // 8 = ParamLine's flex gap
    : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 6 }}>
      {formulaParams.map(sp => {
        const entry = fields.param_map[sp.name];
        const hasEntry = sp.name in fields.param_map;
        const raw = entry == null ? '' : String(entry);
        const errRef = {};
        const resolved = hasEntry && raw.trim()
          ? evalFormula(raw, parentTypicalEnv, errRef)
          : sp.typical_value;
        return (
          <ParamLine
            key={sp.name}
            p={{ ...sp, typical: sp.typical_value }}
            value={sp.type === 'boolean'
              ? (hasEntry && raw.trim() ? Number(raw) : Number(sp.typical_value))
              : raw}
            // A BOOLEAN gets Function's checkbox (Alex, 2026-08-04: "in product, can the
            // yes/no be the checkmark in function?") -- "typical: 0" in a text box is a
            // poor way to ask a yes/no question. Everything else keeps the one box: a
            // mapping is not "what value?" but "what expression over the PARENT's
            // parameters?", and a list here may have no options at all to pick from
            // (sub-product 728's Conduit is exactly that).
            //
            // The checkbox costs the third state -- it cannot say "leave it at the
            // typical" the way an empty box can -- so onClearToTypical below gives it
            // back explicitly. That was the reason this was a text box yesterday.
            allowFormula={sp.type !== 'boolean'}
            atTypicalOverride={!hasEntry || !raw.trim()}
            nameWidth={nameWidth}
            // Only where clearing is not otherwise expressible. A text box says
            // "typical" by being empty; a checkbox has no empty, so it needs the
            // explicit way back.
            onClearToTypical={sp.type === 'boolean' ? (name => unsetParamMapRow(name)) : undefined}
            onChange={(name, v) => {
              // Empty IS the way back to typical — drop the entry entirely rather than
              // storing a blank, so the map stays sparse and the resolver keeps its
              // "absent means the sub's own typical" rule.
              const str = v == null ? '' : String(v);
              if (!str.trim()) unsetParamMapRow(name); else setParamMapRow(name, str);
            }}
            after={
              <Text kind="note">
                resolves to {resolved != null
                  ? formatParamValue(resolved)
                  : <span style={{ color: 'var(--th-status-deprecated)' }}>⚠ {errRef.msg || 'unresolved'}</span>}
                {sp.unit ? ` ${sp.unit}` : ''}
              </Text>
            }
          />
        );
      })}
    </div>
  );
}

// The size-less sentinel. `size_lists` holds a real row list_name='unique',
// size_id='unique', look='', nps=0 so that a family with no size axis still joins
// (see catalog-db "the singleton 'unique' ladder"). The tree/comp line projections
// do not carry size_id, so it is identified by the value pair only it has: nps 0
// with no look. Verified 2026-08-03 against all 10 size lists — no other row has
// nps=0 or an empty look, and no 'unique' family has a line with another size_id.
function isUniqueSize(l) {
  return Number(l?.size_value) === 0 && !String(l?.line_size || '').trim();
}

// SizeEditor — the Size control innards (unified sized-family dropdown, or the
// legacy multi-control fallback for existing/product comps). Returns a fragment;
// each consumer wraps it in a flex container.
function SizeEditor(E) {
  const {
    fields, set, setFields, colStyle, roStyle, familyDisplay, selectedFamily, sizeFamily, sizeParams, searchFilters, onCompSearch, sig,
    subSizeParams, sizeListValuesCache, setParamMapRow, unsetParamMapRow,
  } = E;
  return (
    <>
      {fields.kind === 'line' && sizeFamily?.isSized ? (
        <label style={{ ...colStyle, width: 220 }}>
          <CascadeDropdown
            ariaLabel="Size"
            width={210}
            placeholder="— pick size —"
            allowClear={false}
            openSignal={sig('size')}
            value={
              fields.size_expr && /^[a-zA-Z_]\w*$/.test(fields.size_expr)
                ? `param:${fields.size_expr}`
                : (fields.size_expr ? `size:${fields.size_expr}` : '')
            }
            options={[
              ...sizeFamily.sizedLines.map(l => {
                // "Unique", not 0" (Alex, 2026-08-03: "When size is unique, is it
                // possible to say Unique instead of zero... I always don't understand").
                // The size-less sentinel is a REAL size_lists row (list_name='unique')
                // carrying look:'' and nps:0 — so the empty look fell through to the
                // `${nps}"` fallback and every size-less family offered one option
                // reading 0". Measured: the sentinel is the only row in all 10 size
                // lists with nps=0, and it covers 506 families.
                const look = isUniqueSize(l)
                  ? 'Unique'
                  : (l.line_size || `${l.size_value}"`).trim().replace(/\s+/g, ' ');
                return {
                  code: `size:${l.size_value}`,
                  display: look,
                  label: '',
                  hint: l.price != null ? rate(l.price, l.unit || '') : undefined,
                };
              }),
              ...sizeParams.map(p => ({
                code: `param:${p.name}`,
                display: p.name,
                label: p.type === 'list' ? '(size param — follow)' : '(param — bind to config)',
              })),
            ]}
            onChange={(val) => {
              if (!val) { setFields(f => ({ ...f, size_mode: 'sized', size_expr: '' })); return; }
              if (val.startsWith('param:')) {
                set('size_expr', val.slice(6)); set('size_mode', 'sized');
              } else if (val.startsWith('size:')) {
                set('size_expr', val.slice(5)); set('size_mode', 'sized');
              }
            }}
          />
        </label>
      ) : (fields.kind === 'line' && selectedFamily && !selectedFamily.isSized) ? (
        null
      ) : (fields.kind === 'product' && subSizeParams.length > 0) ? (
        // S3-size-unify — the sub-product's own size param(s) get the SAME
        // catalog-style fixed/follow dropdown as a sized catalog-line comp above,
        // writing param_map instead of size_expr (Q8v2 contract, unchanged): a
        // fixed pick → numeric constant, a follow pick → the parent param's name,
        // typical (cleared) → the param_map row removed. The sub's OTHER params
        // stay in the formula mapping table (ParamMappingTable, below).
        <>
          {subSizeParams.map(subSize => {
            const subSizeList = sizeListValuesCache[subSize.size_unit] || [];
            const compatibleParents = sizeParams.filter(p => p.size_unit === subSize.size_unit);
            const raw = (fields.param_map || {})[subSize.name];
            const trimmed = raw != null ? String(raw).trim() : '';
            const value = /^-?\d*\.?\d+$/.test(trimmed) ? `fixed:${trimmed}`
              : /^[a-zA-Z_]\w*$/.test(trimmed) ? `follow:${trimmed}`
              : '';
            return (
              <label key={subSize.name} style={{ ...colStyle, width: 220 }}>
                <CascadeDropdown
                  ariaLabel="Size"
                  width={210}
                  placeholder="— typical —"
                  allowClear
                  openSignal={sig('size')}
                  value={value}
                  options={[
                    ...subSizeList.map(o => ({ code: `fixed:${o.num}`, display: o.label, label: '' })),
                    ...compatibleParents.map(p => ({ code: `follow:${p.name}`, display: p.name, label: '(follow product size)' })),
                  ]}
                  onChange={(val) => {
                    if (!val) { unsetParamMapRow(subSize.name); return; }
                    if (val.startsWith('fixed:')) setParamMapRow(subSize.name, val.slice(6));
                    else if (val.startsWith('follow:')) setParamMapRow(subSize.name, val.slice(7));
                  }}
                />
              </label>
            );
          })}
        </>
      ) : (
        <>
          <label style={{ ...colStyle, width: 130 }}>
            <SelectCell
              value={fields.size_mode}
              title="Size"
              onChange={next => {
                const wasSized = fields.size_mode === 'sized';
                const willBeSized = next === 'sized';
                if (wasSized !== willBeSized) {
                  setFields(f => ({ ...f, size_mode: next, ref_id: '', ref_label: '' }));
                  onCompSearch('', undefined, searchFilters);
                } else {
                  set('size_mode', next);
                }
              }}
              // TWO options, because a component can only be in two states: it
              // names one catalog size (pinned) or it computes one (sized).
              // 'Fixed value' and '= formula' were removed 2026-07-29 with the
              // size_mode column — 'Fixed value' had 0 rows ever, and
              // '= formula' fed its expression to the QUANTITY, not the size,
              // which is not what the option said it did.
              options={[
                { value: 'NA', label: 'NA' },
                { value: 'sized', label: '= param (sized)' },
              ]}
            />
          </label>

          {fields.size_mode === 'sized' && (
            <label style={{ ...colStyle, width: 110 }} title="Family">
              {!fields.ref_id ? (
                <span style={{ fontSize: 'var(--fs-small)', color: 'var(--th-warn-accent)', paddingBottom: 3 }}>
                  ↑ pick a line
                </span>
              ) : (
                <span style={{ ...roStyle, fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', minWidth: 100 }}>
                  {familyDisplay}
                </span>
              )}
            </label>
          )}

          {fields.size_mode === 'sized' && (
            <label style={{ ...colStyle, width: 170 }} title="Param / formula">
              <TextCell
                value={fields.size_expr}
                onChange={v => set('size_expr', v)}
                placeholder="e.g. Size"
              />
            </label>
          )}
        </>
      )}
    </>
  );
}

// QtyEditor — the qty formula input PLUS the S3d live-resolved preview ("weight in
// line with qty"): evalFormula(qty_expr, env) at the row's current params, shown
// next to the formula. Read-only preview — surfaces the already-computed number.
function QtyEditor(E, row) {
  const { fields, set, env } = E;
  const expr = (fields.qty_expr || '').trim();
  let preview = null, previewErr = null;
  if (expr) {
    const errRef = {};
    const v = evalFormula(expr, env, errRef);
    if (v == null) previewErr = errRef.msg || 'unresolved';
    else preview = v;
  }
  // Unit: catalog lines carry line_unit; mass-driven fab lines carry rm_unit ('kg').
  const unit = (row && (row.line_unit || row.rm_unit)) || '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <FormulaCell
        value={fields.qty_expr}
        onChange={v => set('qty_expr', v)}
        placeholder="e.g. 2*Length, 4"
        title="Formula over config params (e.g. 2*Length)."
      />
      {expr && (
        <span
          style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          title={preview != null ? `resolves to ${preview}${unit ? ' ' + unit : ''}` : (previewErr || '')}
        >
          {preview != null
            ? `→ ${formatQty(preview)}${unit ? ' ' + unit : ''}`
            : <span style={{ color: 'var(--th-status-deprecated)' }}>⚠ {previewErr}</span>}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ComponentsGrid — S3d/S3e: the Components DataGrid with IN-CELL editing for BOTH
// edit and add. Owns TWO persistent useCompEditor instances built from the SAME
// pieces:
//   • EDIT (E)    — seeded from the selected comp (reseeds in-render when the
//     selection changes). DataGrid renders each editable column's editor via
//     inlineEditRowId + per-column editRender; the Q8 param table + Save/Cancel live
//     in the renderSubRow extension under the edited row.
//   • ADD (E_add) — S3e: seeded from a BLANK comp so the "+ Add component" ghost row
//     opens the SAME editors in a blank/ghost row's own cells (DataGrid
//     addInlineEditRender) + the param mapping/Add-Cancel in a renderAddSubRow
//     extension below — "ditto" of EDIT, not the old full-width form. Fresh per open
//     via addNonce → comp._seedKey (see the hook's reseed note). The Add save/close
//     reuse handleSave(null,…)/onCompFormSave — the POST payload is byte-identical to
//     the retired full-width CompEditSubRow.
// ---------------------------------------------------------------------------
function ComponentsGrid({
  product, gridRows, gridColumns, categoryTree, catalogTaxonomy, localValues,
  searchQuery, searchResults, onCompSearch,
  onCompFormSave, onDeleteComp, onCopyComp, onReorderComp,
  compFooterRow, selectedCompId, setSelectedCompId, loading, emptyMessage,
  isProductLevel, addFormLabel, locked,
  sizeListValuesCache, fetchSizeListValues,
}) {
  const selectedComp = gridRows.find(r => String(r.id) === String(selectedCompId)) || {};

  // DS-8 (2026-08-21) — a row-adjacent failure slot, matching DataGrid's own
  // built-in saveError (a thrown save handler → a message under the edit bar).
  // ComponentsGrid's edit/add both bypass DataGrid's standard onEditRow/onSave
  // lifecycle (they use the in-cell renderSubRow/renderAddSubRow lane instead), so
  // that built-in mechanism never sees them — this is the same idea, owned here.
  // Cleared on every new save ATTEMPT (below) and whenever which row is open
  // changes, so a stale message from a different row/attempt never lingers.
  const [compSaveError, setCompSaveError] = useState(null);
  useEffect(() => { setCompSaveError(null); }, [selectedCompId]);

  // Save via the shared handler; on success (edit) it reloads + clears selection.
  // On failure (handleCompFormSave/handleSaveEditComp now THROW, DS-8) the row
  // stays open and the message renders in renderSubRow, below Save/Cancel.
  const onSave = async (compId, fields) => {
    setCompSaveError(null);
    try {
      const ok = await onCompFormSave(compId, fields);
      if (ok) setSelectedCompId(null);
      return ok;
    } catch (err) {
      setCompSaveError(err.message || 'Could not save this component.');
      return false;
    }
  };

  const E = useCompEditor({
    comp: selectedComp, params: product?.params || [], categoryTree, onSave,
    searchQuery, searchResults, onCompSearch, catalogTaxonomy, localValues, product,
    sizeListValuesCache, fetchSizeListValues,
  });

  // S3e — ADD editor. Same hook + same pieces, seeded blank. onAddSave routes to the
  // add POST (compId null) and returns the success boolean so the Add button can close
  // the ghost row only on an ACTUAL add (matching the retired form). addNonce (bumped on
  // open, below) flows through comp._seedKey so the persistent instance reseeds blank
  // each open — and clears compSaveError (DS-8) for the same reason selectedCompId does.
  const [addNonce, setAddNonce] = useState(0);
  useEffect(() => { setCompSaveError(null); }, [addNonce]);
  const onAddSave = async (_compId, fields) => {
    setCompSaveError(null);
    try {
      return await onCompFormSave(null, fields);
    } catch (err) {
      setCompSaveError(err.message || 'Could not add this component.');
      return false;
    }
  };
  const E_add = useCompEditor({
    comp: { _seedKey: addNonce }, params: product?.params || [], categoryTree, onSave: onAddSave,
    searchQuery, searchResults, onCompSearch, catalogTaxonomy, localValues, product,
    sizeListValuesCache, fetchSizeListValues,
  });

  // Per-column editRender — ONE builder, bound to whichever editor instance (E for the
  // edited row, E_add for the blank add row) so the two paths can never diverge.
  // S3i — the DataGrid <td> hosting each editRender sets verticalAlign:'middle' (out of
  // scope — DataGrid.jsx isn't touched here), so when the row grows tall (any cell's
  // content taller than another, e.g. the Qty formula+preview stack vs a one-line
  // dropdown) the shorter editors sat vertically centered in that tall row. A plain
  // height:'100%' on the wrapper div doesn't fix this — CSS only resolves a percentage
  // height against a parent whose OWN height was explicitly specified, and the <td>'s
  // height here is purely content-derived (verified live: the wrapper's computed height
  // stayed its natural content height, not the td's actual row height). So instead this
  // marks each wrapper with the EDIT_TOP_CLASS and a single <style> rule (below, in the
  // DataGrid return) targets `td:has(> .EDIT_TOP_CLASS)` directly — overriding just the
  // handful of <td>s that host an in-cell editor, without touching DataGrid.jsx.
  const EDIT_TOP_CLASS = 's3i-edit-top';
  const cellTop = { display: 'flex', alignItems: 'flex-start' };
  const pickWrap = { ...cellTop, flexWrap: 'wrap', gap: 6 };
  // S3h — Source + L1▸L2▸L3 merged into ONE cell (key 'ref_id'), all inline on a single
  // row (Alex: "all four in the same line"). Was a column (Source stacked above the
  // cascade, S3g); now a row — the cascade itself is already nowrap (wrap={false} in
  // ComponentRefEditor), so this puts Source at the front of the same line instead of
  // on its own line above.
  // S3i — flexWrap flipped nowrap→wrap so this group wraps/stacks the same way the
  // Component cell (pickWrap, also flexWrap:'wrap') already does when the grid narrows,
  // instead of staying rigidly one-line while Component stacks. Source and the
  // L1▸L2▸L3 cascade (itself still wrap={false} — the 3 levels stay together as one
  // unit) are the two wrap-able pieces, so on narrow they stack Source over the
  // cascade — same "stack when tight" behavior as Component's pieces.
  //
  // S3j (2026-08-21) — ComponentPickEditor (the item/product LEAF pick) now renders
  // in THIS SAME CELL, right after the L1▸L2▸L3 cascade, instead of in the separate
  // 'label' column. Alex: picking a component "looks like a two-step cascade" — it
  // wasn't the CascadeSelect/CascadeDropdown split itself (that's invisible; a
  // CascadeSelect IS a row of CascadeDropdowns), it was that the level cascade and
  // the leaf pick that completes it sat in two DIFFERENT GRID CELLS, so the eye had
  // to cross a column boundary mid-pick. Same components, same shared useCompEditor
  // state/auto-advance signal as before — only WHERE they render changed. The
  // 'label' column no longer gets an editRender for this row, so DataGrid falls back
  // to its normal (non-edit) render there — the currently-saved label + CompInputs/
  // MappingFeedback — a live "here's what's set" while you pick its replacement here.
  const srcRefWrap = { ...cellTop, flexDirection: 'row', flexWrap: 'wrap', gap: 4 };
  const makeEditRenderByKey = (ed) => ({
    description: () => <div className={EDIT_TOP_CLASS} style={cellTop}>{DescriptionEditor(ed)}</div>,
    ref_id: () => <div className={EDIT_TOP_CLASS} style={srcRefWrap}>{SourceEditor(ed)}{ComponentRefEditor(ed)}{ComponentPickEditor(ed)}</div>,
    size_display: () => <div className={EDIT_TOP_CLASS} style={pickWrap}>{SizeEditor(ed)}</div>,
    effective_qty: (row) => <div className={EDIT_TOP_CLASS} style={cellTop}>{QtyEditor(ed, row)}</div>,
  });
  const editRenderByKey = makeEditRenderByKey(E);
  // editColSpan (DataGrid, 2026-08-21) — Ref reclaims Label's width while editing
  // (S3j's real-estate follow-up: Label has nothing useful to show mid-pick, and
  // the Source+L1▸L2▸L3▸item+search line needs the room). 'label' is the very next
  // column after 'ref_id' in makeGridCols — DataGrid skips exactly that <td> for
  // the edited/add row, not painted-over, actually reclaimed.
  const EDIT_COL_SPAN = { ref_id: 2 };
  const columns = gridColumns.map(c => editRenderByKey[c.key]
    ? { ...c, editRender: editRenderByKey[c.key], ...(EDIT_COL_SPAN[c.key] ? { editColSpan: EDIT_COL_SPAN[c.key] } : null) }
    : c);
  // Is this comp id one the Onshape sync owns? See the DataGrid props below.
  const syncOwned = id => gridRows.find(r => String(r.id) === String(id))?.source === 'onshape';
  // In-cell ADD cells (blank row) — same pieces, bound to E_add. Keyed by column so
  // DataGrid drops each editor under its own header, exactly like EDIT.
  const addInlineEditRender = makeEditRenderByKey(E_add);

  // Extension row (Q8 param table + Save/Cancel) under the edited component.
  // Alex, 2026-08-04: "adjust the layout to have all the input data almost in line".
  // The parameter lines used to start at the sub-row's left edge, so their boxes sat
  // under the icon columns while every other input on the row (Size, Qty) sat far to
  // the right. Indenting to the COMPONENT column puts the parameter names under the
  // component they belong to and their boxes under Size/Qty -- the same trick the
  // Function screen uses to line its config block up under Configuration, and it
  // follows a column resize because offsetOf reads the live widths.
  //
  // DS-7 (2026-08-21) — the Save/Cancel row used to live INSIDE that same indented
  // wrapper and got dragged along with it: every other grid's edit bar sits flush
  // left (measured x=52, right after the icon columns — Product header, Configuration,
  // DataGrid's own built-in bar all agree), but this one sat at x=554, under the
  // Component column. The indent belongs to the PARAMETER TABLE alone; the button row
  // now gets its own plain inline padding, matching the base case everywhere else.
  const renderSubRow = (_row, ctx) => (
    <div style={{ padding: '8px 0', background: 'var(--th-row-selected)', borderLeft: '3px solid var(--th-row-selected-border)' }}>
      <div style={{ paddingInlineStart: (ctx?.offsetOf?.('label') ?? 8), paddingInlineEnd: 8 }}>
        {ParamMappingTable(E, ctx)}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', paddingInline: 8, marginTop: E.subMappableParams.length > 0 ? 8 : 0 }}>
        <button onClick={E.handleSave} disabled={E.saving || !E.fields.ref_id}
          style={{ ...BTN_PRIMARY, ...(E.saving ? BTN_DISABLED : null) }}
        >{E.saving ? '…' : 'Save'}</button>
        <button onClick={() => setSelectedCompId(null)} style={BTN_SECONDARY}>Cancel</button>
        <Text kind="note">
          Editing component — Save to commit, Cancel to revert.
        </Text>
      </div>
      {/* DS-8 — same placement as DataGrid's own built-in saveError: a separate
          line directly under the Save/Cancel bar, not a page-level banner. */}
      {compSaveError && (
        <div style={{ paddingInline: 8, marginTop: 6 }}>
          <Notice inline onDismiss={() => setCompSaveError(null)}>{compSaveError}</Notice>
        </div>
      )}
    </div>
  );

  // S3e — ADD extension row (Q8 param table + Add/Cancel), under the blank add row.
  // Add: handleSave builds the SAME { kind, ref_id, …common } payload and returns the
  // add-success boolean, so the ghost row closes only on an actual add. Cancel clears the
  // search buffer (like the retired form's onCancel) and closes.
  // DS-7 — same button-row un-indent as renderSubRow above.
  const renderAddSubRow = (close, ctx) => (
    <div style={{ padding: '8px 0', background: 'var(--th-row-selected)', borderLeft: '3px solid var(--th-row-selected-border)' }}>
      <div style={{ paddingInlineStart: (ctx?.offsetOf?.('label') ?? 8), paddingInlineEnd: 8 }}>
        {ParamMappingTable(E_add, ctx)}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', paddingInline: 8, marginTop: E_add.subMappableParams.length > 0 ? 8 : 0 }}>
        <button onClick={async () => { const ok = await E_add.handleSave(); if (ok) close(); }} disabled={E_add.saving || !E_add.fields.ref_id}
          style={{ ...BTN_PRIMARY, ...(E_add.saving ? BTN_DISABLED : null) }}
        >{E_add.saving ? '…' : 'Add'}</button>
        <button onClick={() => { onCompSearch('', undefined, { l1: '', l2: '', l3: '' }); close(); }} style={BTN_SECONDARY}>Cancel</button>
        <Text kind="note">
          Add component — Add to save, Cancel to discard.
        </Text>
      </div>
      {compSaveError && (
        <div style={{ paddingInline: 8, marginTop: 6 }}>
          <Notice inline onDismiss={() => setCompSaveError(null)}>{compSaveError}</Notice>
        </div>
      )}
    </div>
  );

  // Bump the add nonce whenever the ghost row opens → E_add reseeds blank for a fresh add.
  // The reverse direction of DataGrid's "one open thing": this grid's row editor is
  // OUR state (selectedCompId + inlineEditRowId), which DataGrid cannot see or close,
  // so opening the add row has to clear it here. Without this, Add and Edit sat open
  // together with two Save bars (Alex, 2026-08-01).
  const onAddOpenChange = (open) => { if (open) { setAddNonce(n => n + 1); setSelectedCompId(null); } };

  return (
    <>
      {/* S3i — top-aligns the in-cell editors: see the EDIT_TOP_CLASS note above
          makeEditRenderByKey. Scoped to only the <td>s that directly host one of our
          editRender wrappers — no other grid/cell is affected. */}
      <style>{`td:has(> .${EDIT_TOP_CLASS}) { vertical-align: top !important; }`}</style>
      <DataGrid
        rows={gridRows}
        columns={columns}
        // INF-14 — a locked product's components cannot be opened for edit: selecting
        // a row IS the edit here. Copy still works on each row.
        onRowClick={locked ? undefined : (row => setSelectedCompId(row.id === selectedCompId ? null : row.id))}
        rowLocked={locked ? () => locked : undefined}
        selectedRowId={selectedCompId}
        // A synced component is Onshape's, not ours (Alex, 2026-08-03). The server
        // refuses the PATCH and the DELETE either way; these two just stop the app
        // OFFERING an action it will then reject. Selecting the row still works —
        // you can read it, copy it, and edit the copy.
        inlineEditRowId={syncOwned(selectedCompId) ? null : selectedCompId}
        loading={loading}
        emptyMessage={emptyMessage}
        // …and SAY SO, rather than only refusing (Alex, 2026-08-03: "Onshape row
        // could also have a greyish background to understand they are not editable.
        // And the Onshape icon instead of delete"). The lock was enforced but
        // invisible — you found out by clicking and being told no. Background +
        // the cube in the delete column make it readable before the click, and
        // neither is a colour-only signal: the icon carries the meaning.
        // ⚠ getRowStyle is spread LAST in DataGrid's trStyle, so it beats the
        // selected-row background. Skip the SELECTED row or clicking a synced
        // component would stop looking selected — you can still read and copy
        // one, and selection is how you do it.
        // ERR-1(a) — a row whose value did not resolve says so with its whole
        // background (Alex, 2026-08-03: "When something as an error, everywhere, can
        // the background also becomes soft red?"). It does NOT replace the ⚠ and the
        // message already in the row: colour is never the only signal here, it is what
        // makes the bad row findable when you are scanning thirty of them.
        // Error beats locked — a synced row that fails to resolve is a problem first.
        getRowStyle={row => (row.formula_error
          ? { background: 'var(--th-row-error)' }
          : row.source === 'onshape' && String(row.id) !== String(selectedCompId)
            ? { background: 'var(--th-row-locked)' }
            : undefined)}
        deleteBlockedIcon={row => (row.source === 'onshape'
          // title on a WRAPPER span, not on the <svg> — an SVG's tooltip is a
          // <title> CHILD element, so a title attribute is unreliable there.
          ? <span title="From the Onshape sync — delete it in Onshape and re-sync"><OnshapeMark size={12} /></span>
          : null)}
        onDelete={onDeleteComp}
        onDeleteCheck={row => (row.source === 'onshape'
          ? 'This component comes from the Onshape sync — delete it in Onshape and re-sync.'
          : null)}
        onCopy={onCopyComp}
        onReorder={locked ? undefined : onReorderComp}
        footerRow={compFooterRow}
        storageKey="product-comps"
        renderSubRow={renderSubRow}
        {...(isProductLevel && !locked ? { addFormLabel, addInlineEditRender, renderAddSubRow, onAddOpenChange } : {})}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// PathDropdowns — cascading ancestor selects for move / re-parent (R2/R3/R4/R7)
// disabled=true (outside edit mode) → shows current path read-only; dropdowns are inert.
// Options: Tag (fixed-width mono) + Label on one line so descriptions align (R4).
// ---------------------------------------------------------------------------
// onNavigate (breadcrumb retirement, 2026-07-20): in READ mode the path
// renders as clickable crumbs that navigate up the hierarchy — the breadcrumb's
// job absorbed by the Parent cell (§5: "the breadcrumb and the move cascade are
// the same information — one read-only, one editable"). Omit it and read mode
// stays plain text (grid rows that don't want the affordance).
// onPick — STAGE the pick instead of performing it, replacing onMove entirely when
// present. Same seam as CatalogView's CatalogReparent, added for the same reason
// (Alex, 2026-08-27, after the Catalog fix: "I would like to avoid being upset with
// inconsistency"): every other cell in these rows buffers until Save, and this one
// wrote straight to the server on pick, so Save had nothing to do and Cancel could
// not undo it. All THREE call sites here sit inside buffered grids, so all three
// stage — the products screen had the defect in triplicate.
function PathDropdowns({ row, categoryTree, depth, onMove, onPick, disabled, onNavigate }) {
  const [l1, setL1] = useState(() => row._l1Id ?? null);
  const [l2, setL2] = useState(() => row._l2Id ?? null);
  const [l3, setL3] = useState(() => row._l3Id ?? null);
  // AUTO-ADVANCE (Alex, 2026-08-03: "Why this drop down and same in parent data
  // grid does not auto cascade?"). It is rule 4 of the cascade — picking a level
  // opens the next one, not optional — and this control was the last one still
  // missing it. One place fixes BOTH the product header's Parent cell and the
  // hierarchy grid's, because both render this same component.
  //
  // 2026-08-04 -- MIGRATED to useCascadeSignal, and this is the site the hook exists
  // for. These were per-level counters starting at 0, so every level mounted holding a
  // real value; that is indistinguishable from "just bumped", and it is exactly how
  // this control came to open itself the moment a row entered edit mode. The hook's
  // signals are undefined until targeted, so the failure is not expressible here.
  const cascade = useCascadeSignal();
  const advance = cascade.advance;

  // CANCEL MUST PUT THE PATH BACK. The drafts live in this component, and leaving
  // edit mode does NOT remount it (the consumer's `key` only changes when the
  // SERVER path does), so a cancelled pick stayed on screen: the cell read
  // "06 > — > —" while the product was still at 05 > 01 > 03. Nothing was ever
  // written -- only the DISPLAY lied, which is worse, not better. Found while
  // testing the auto-advance above, which makes it trivial to hit (one pick now
  // clears two levels). Re-seed from the row on entering AND leaving edit mode,
  // and whenever the row genuinely moves.
  useEffect(() => {
    setL1(row._l1Id ?? null);
    setL2(row._l2Id ?? null);
    setL3(row._l3Id ?? null);
  }, [disabled, row._l1Id, row._l2Id, row._l3Id]);

  const l1Node = l1 != null ? categoryTree.find(n => n._catId === l1) : null;
  const l2Opts = l1Node ? (l1Node.children || []).filter(c => !c.isProduct) : [];
  const l2Node = l2 != null ? l2Opts.find(n => n._catId === l2) : null;
  const l3Opts = l2Node ? (l2Node.children || []).filter(c => !c.isProduct) : [];

  // The pick that IS the move for this row's depth. Staged when the caller can
  // stage it; performed only where it cannot. Levels above `depth` are navigation
  // within the cascade and never commit anything — that is rule 4, unchanged.
  const commit = (id) => { if (onPick) onPick(id); else onMove(id); };
  const handleL1 = v => {
    if (disabled) return;
    const id = v ? Number(v) : null;
    setL1(id); setL2(null); setL3(null);
    if (depth === 1 && id) commit(id);
    else if (id) advance('l2');   // rule 4 — a pick opens the next level
  };
  const handleL2 = v => {
    if (disabled) return;
    const id = v ? Number(v) : null;
    setL2(id); setL3(null);
    if (depth === 2 && id) commit(id);
    else if (id) advance('l3');
  };
  const handleL3 = v => {
    if (disabled) return;
    const id = v ? Number(v) : null;
    setL3(id);
    if (depth === 3 && id) commit(id);
  };

  // In read-only mode, show the current path — the shared dense Breadcrumb
  // (clickable when onNavigate is provided, plain spans otherwise).
  if (disabled) {
    const l1Label = l1Node ? l1Node.code : (l1 != null ? `#${l1}` : '—');
    const l2Label = l2Node ? l2Node.code : (l2 != null ? `#${l2}` : '—');
    const l3Node = l3 != null ? l3Opts.find(n => n._catId === l3) : null;
    const l3Label = l3Node ? l3Node.code : (l3 != null ? `#${l3}` : '—');
    const parts = [
      { label: l1Label, nodeId: l1 != null ? `c1-${l1}` : null },
      ...(depth >= 2 ? [{ label: l2Label, nodeId: l2 != null ? `c2-${l2}` : null }] : []),
      ...(depth >= 3 ? [{ label: l3Label, nodeId: l3 != null ? `c3-${l3}` : null }] : []),
    ];
    const crumbs = parts.map(p => ({ label: p.label, onClick: (onNavigate && p.nodeId != null) ? () => onNavigate(p.nodeId) : undefined }));
    return <Breadcrumb dense crumbs={crumbs} />;
  }

  // P5r: cascading levels render via the shared two-column (code | label) CascadeSelect
  // (levels-only — no item/size), matching the picker's style. Each level's onChange
  // still resolves to a numeric category id and calls onMove exactly as before.
  const toOptions = opts => opts.map(n => ({ code: n._catId, label: n.label || '', display: n.code || '' }));
  const levels = [];
  if (depth >= 1) {
    levels.push({
      ariaLabel: 'Level 1 category', width: 170, allowClear: false,
      placeholder: '— pick parent —',
      value: l1, options: toOptions(categoryTree), onChange: handleL1,
    });
  }
  if (depth >= 2) {
    levels.push({
      ariaLabel: 'Level 2 category', width: 170, allowClear: false,
      placeholder: '— pick parent —',
      value: l2, disabled: !l1, options: toOptions(l2Opts), onChange: handleL2,
      openSignal: cascade.signalFor('l2'),
    });
  }
  if (depth >= 3) {
    levels.push({
      ariaLabel: 'Level 3 category', width: 170, allowClear: false,
      placeholder: '— pick parent —',
      value: l3, disabled: !l2, options: toOptions(l3Opts), onChange: handleL3,
      openSignal: cascade.signalFor('l3'),
    });
  }

  return <CascadeSelect levels={levels} gap={3} />;
}

// ProductOnshapeLink — the product-leaf half of "add when empty" (UI_VISION §0,
// phase 2). The Onshape section used to be hidden ENTIRELY when a product had no
// ref, so there was no way to link one from the product page at all — Alex:
// "Even if there is nothing in Onshape, we still need that kind of title with a
// way to add... and we should be able to link it."
//
// Deliberately modelled on CatalogView's FamilyOnshapePanel, which STATUS names
// as the reference pattern: the editor renders WHETHER OR NOT the item is
// linked, so a wrong ref can be corrected, not just an empty one filled. A
// product carries only `onshape_ref` — onshape_config/onshape_size are family
// concepts (family = config, line = size), so there is one field here, not three.
//
// The raw string is sent as typed; the server normalizes a pasted document URL
// (normalizeOnshapeRefOrThrow) exactly as it does for families. No second
// parsing rule client-side.
function ProductOnshapeLink({ productId, onshapeRef, onSaved }) {
  const [draft, setDraft] = useState(onshapeRef || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setDraft(onshapeRef || ''); setError(null); }, [productId, onshapeRef]);

  const dirty = draft !== (onshapeRef || '');
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/products/${productId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ onshape_ref: draft.trim() }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Save failed'); }
      await onSaved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>
        Onshape ref (did/wid/eid)
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="did/wid/eid"
          style={{ fontSize: 'var(--fs-small)', padding: '3px 6px', border: '1px solid var(--th-border-subtle)', borderRadius: 3, width: 340, fontFamily: 'var(--ff-mono)' }}
        />
      </label>
      <button
        onClick={save}
        disabled={!dirty || saving}
        style={{ ...BTN_PRIMARY, ...((!dirty || saving) ? BTN_DISABLED : null) }}
      >{saving ? 'Saving…' : 'Save'}</button>
      {/* COS-1(b): every grid row already gets Save+Cancel — this editor was the
          outlier, offering only Save with no way back to the stored value. */}
      {dirty && !saving && (
        <button onClick={() => { setDraft(onshapeRef || ''); setError(null); }} style={BTN_SECONDARY}>Cancel</button>
      )}
      {error && <Text kind="deprecated">{WARN} {error}</Text>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProductsView
// ---------------------------------------------------------------------------
// Width of the Simulate column AND of the Reset button above it — one constant so
// they cannot drift out of alignment.
const PARAM_SIMULATE_W = 110;

export default function ProductsView({ view, setView, onNavigateToCatalog, selectedNodeId, onSelectNode, onNavigateToPlacement, hideNav = false }) {
  // ERR-1 — action failures render in the app, next to the work, instead of in a
  // browser popup. Follows the house rule already stated in HistoryPanel.jsx:
  // "never window.confirm/alert".
  const [actionError, setActionError] = useState(null);
  // ERR-1(b) — a row's failure is no longer tracked here. A save handler THROWS and
  // DataGrid places the message under the row it belongs to. actionError stays for
  // view-level failures that have no row to sit under (a failed reload, an add with
  // nothing selected) — two kinds of error, but only one of them is this view's to place.
  const [product, setProduct] = useState(null);
  const [localValues, setLocalValues] = useState({});
  // The values actually in force right now — typicals, overlaid with whatever is
  // being simulated, with the derived params computed from both. THE shared builder,
  // so the number shown next to a formula is the number the cost engine used.
  const liveParamValues = useMemo(
    () => buildParamEnv(product?.params || [], localValues).env,
    [product?.params, localValues],
  );
  const [loading, setLoading] = useState(false);
  const [selectedCompId, setSelectedCompId] = useState(null);
  const [saving, setSaving] = useState(false);

  // "Where used" panel — read-only usage report for the selected product.
  const [usage, setUsage] = useState(null); // { placements, components } | null
  const [usageLoading, setUsageLoading] = useState(false);

  // Bumped when a Preview lands inside the Onshape section — the server has just
  // rewritten `onshape_sync_issues`, so the Sync worklist section (further down
  // the page, beside COMPONENTS) must re-read. The two are no longer nested, so
  // the token lives here, where both can see it.
  const [worklistToken, setWorklistToken] = useState(0);

  // The open Not-synced COUNT, shown on the COMPONENTS title (Alex, 2026-08-21:
  // the warning "doesn't belong to that Onshape window... maybe besides the
  // title components"). Re-read on the same token a sync bumps.
  const [notSyncedCount, setNotSyncedCount] = useState(0);

  // After a param changes, rerun the config comparison against the CACHED
  // Onshape answer (0 calls — peekJson never fetches) and nudge the review
  // panel. Alex, 2026-08-21: he added the param a finding asked for and the
  // finding just sat there — "I do not know if I have to spend more calls".
  const recheckConfigFromCache = useCallback((pid) => {
    if (!pid) return;
    fetch(`/api/products/${pid}/config-recheck-cached`, { method: 'POST' })
      .then(r => (r.ok ? r.json() : null))
      .then(b => { if (b && b.cached) setWorklistToken(t => t + 1); })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!product?.id) { setNotSyncedCount(0); return; }
    let live = true;
    fetch(`/api/products/${product.id}/sync-issues`)
      .then(r => (r.ok ? r.json() : []))
      .then(list => { if (live) setNotSyncedCount(Array.isArray(list) ? list.filter(i => i.status === 'open').length : 0); })
      .catch(() => {});
    return () => { live = false; };
  }, [product?.id, worklistToken]);

  // Phase 1c — Onshape panel (thumbnail / 3D viewer / manual mass sync).
  // No ghost calls: thumbnail and viewer only load on explicit user click.

  // BUILD 1 — product authoring
  const [productFormMode, setProductFormMode] = useState(null); // null | 'create' | 'edit'
  // placeable defaults FALSE (Alex, 2026-08-05: "By default a product should not be
  // placeable and I think it is the opposite!"). It means "finished — a customer can have
  // this", so defaulting it ON offered every half-built draft the moment it was created.
  // That is how 170 of 173 products became placeable without anyone deciding it, and why
  // the flag cannot currently be trusted for anything.
  // ⚠ The COLUMN still reads `DEFAULT 1`; changing that is a schema change and needs
  // Alex's explicit approval, so it is deliberately not done here. This fixes every
  // product created through the UI; an INSERT that omits the column still gets 1.
  const [productFormFields, setProductFormFields] = useState({ code: '', label: '', placeable: false, status: 'draft', trade: '' });
  const [savingProduct, setSavingProduct] = useState(false);
  // S1b: size_unit/customOptions/typical_text are only used when kind='list' —
  // customOptions is the in-progress {value,label} array for size_unit='custom'
  // (JSON-encoded only on submit); typical_text is the picked option's value.
  // configurable/value_expr belong here too (2026-08-04). The add form predated the
  // Config? flag, so EVERY parameter created through the UI was born a knob and had
  // to be edited internal afterwards — and on a quantity-based product that second
  // step is not cosmetic, it is the difference between a legal shape and a refused one.
  const NEW_PARAM_INIT = { name: '', type: 'count', typical_value: '', unit: '', min: '', max: '', description: '', size_unit: '', customOptions: [], typical_text: '', configurable: '1', value_expr: '' };
  const [newParam, setNewParam] = useState(NEW_PARAM_INIT);
  // S1b — scratch inputs for the "add a custom option" mini-editor (value/label pair,
  // cleared after each Add click). Not part of newParam itself — those are just the
  // in-progress text fields for the NEXT option to append.
  const [newOptionDraft, setNewOptionDraft] = useState({ value: '', label: '' });
  const [savingParam, setSavingParam] = useState(false);

  // ProductHierarchyGrid — category tree + children grid
  const [categoryTree, setCategoryTree] = useState([]);
  // PROJECT ONLY (Alex, 2026-10-15) — same rule as CatalogView: working in a project,
  // the tree and grid start as its OWN products; the "Standard" box brings the rest back.
  // Display-only: categoryTree stays whole for lookups and the component pickers.
  const { workingIn, workingCode } = useContext(WorkingInContext);
  const [showStandard, setShowStandard] = useState(false);
  const projectOnly = !!workingIn && !showStandard && !hideNav;
  const shownTree = useMemo(() => (projectOnly ? ownOnlyTree(categoryTree, workingIn) : categoryTree), [projectOnly, categoryTree, workingIn]);
  const shownIds = useMemo(() => {
    if (!projectOnly) return null;
    const ids = new Set();
    const walk = (ns) => { for (const n of ns) { ids.add(n.id); walk(n.children || []); } };
    walk(shownTree);
    return ids;
  }, [projectOnly, shownTree]);
  const [catGridRows, setCatGridRows] = useState([]);
  const [catFormOpen, setCatFormOpen] = useState(false);
  const [newCatFields, setNewCatFields] = useState({ code: '', label: '' });
  const [savingCat, setSavingCat] = useState(false);

  // R2: per-row edit mode for the hierarchy grid
  const [editingHierarchyRowId, setEditingHierarchyRowId] = useState(null);
  // §1: selected row in the hierarchy grid (single-click highlight; double-click drills)
  const [selectedHierarchyRowId, setSelectedHierarchyRowId] = useState(null);
  // WS1a-i: configuration (param) grid — click-to-edit row (editOnRowClick lifecycle)
  const [editingParamRowId, setEditingParamRowId] = useState(null);
  // G1: product-info row — same click-to-edit lifecycle as the config grid
  const [editingProductInfoRowId, setEditingProductInfoRowId] = useState(null);

  // BUILD 2 — component authoring
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  // v28: the l1/l2/l3 catalog-search filter state MOVED into CompEditSubRow
  // itself (each form instance is now self-contained); this component only
  // keeps the text-search query/results, wired through onCompSearch.

  // reloadFamilies() is GONE with product_families (2026-07-29). It fetched
  // /api/products/families, which now falls through to /api/products/:id and
  // answers 400 with a JSON error OBJECT — setFamilies then stored a non-array,
  // and `for (const node of families)` in flatFamilies threw "families is not
  // iterable", which is what white-paged this whole screen. A category IS the
  // grouping now; there is no second taxonomy to load.

  // composeTree() in degenerate single-source form — the SAME composition
  // function OnshapeView's composite tree is built from (tree-model.js, Alex
  // 2026-07-20: "the tree used in Onshape [should] be the leader"). A
  // single-source call is a pure passthrough (same array, same ids), so this
  // is a no-op wrapper today; it's what keeps the composition logic in ONE
  // place if a second source is ever needed here.
  const reloadTree = useCallback(() => {
    fetch('/api/products/tree').then(r => r.json())
      .then(prodTree => setCategoryTree(composeTree({ products: prodTree })))
      .catch(console.error);
  }, []);


  // Distinct in-use units for the config-params Unit dropdown (WS1a-ii C5). Loaded once.
  const [unitOptions, setUnitOptions] = useState([]);
  useEffect(() => {
    fetch('/api/catalog/options/units').then(r => r.json()).then(u => setUnitOptions(Array.isArray(u) ? u : [])).catch(console.error);
  }, []);

  // S1b — Size Unit choices for a 'list' config param: distinct size_lists.list_name
  // values (e.g. NPS) + 'custom'. Loaded once, like unitOptions/tradeOptions above.
  const [sizeUnitNames, setSizeUnitNames] = useState([]);
  useEffect(() => {
    fetch('/api/products/options/size-units').then(r => r.json()).then(n => setSizeUnitNames(Array.isArray(n) ? n : [])).catch(console.error);
  }, []);
  const sizeUnitSelectOptions = useMemo(() => [
    ...sizeUnitNames.map(n => ({ value: n, label: n })),
    { value: 'custom', label: 'Custom' },
  ], [sizeUnitNames]);

  // S1b — resolved {value,label} options per standard size_unit (value=size_id,
  // label=look), fetched lazily and cached by list_name. 'custom' never hits this cache
  // (its options live inline on the param itself). Used to render/edit a standard-unit
  // list param's Typical dropdown. `_sizeListFetched` is a plain ref (not state) so a
  // fetch is only ever kicked off once per list_name, regardless of render timing.
  const [sizeListValuesCache, setSizeListValuesCache] = useState({});
  const _sizeListFetched = useRef(new Set());
  const fetchSizeListValues = useCallback((name) => {
    if (!name || name === 'custom' || _sizeListFetched.current.has(name)) return;
    _sizeListFetched.current.add(name);
    fetch(`/api/products/options/size-list-values?name=${encodeURIComponent(name)}`)
      .then(r => r.json())
      .then(opts => setSizeListValuesCache(p => ({ ...p, [name]: Array.isArray(opts) ? opts : [] })))
      .catch(err => { console.error(err); _sizeListFetched.current.delete(name); });
  }, []);
  // Prefetch values for every standard size_unit already in use on this product's
  // params, so the grid shows resolved `look`s (not raw size_ids) on load.
  useEffect(() => {
    const units = new Set((product?.params || []).filter(p => p.type === 'list' && p.size_unit && p.size_unit !== 'custom').map(p => p.size_unit));
    units.forEach(fetchSizeListValues);
  }, [product?.params, fetchSizeListValues]);

  // Trades — one source of truth (Q5). Loaded once for the Trade dropdowns
  // (product-info grid + create form); App.jsx separately feeds the client resolver.
  const [tradeOptions, setTradeOptions] = useState([]);
  // Labour lines (is_labor=1 HR rows) — the product's Labour picker. Loaded once.
  const [laborLineOptions, setLaborLineOptions] = useState([]);
  useEffect(() => {
    fetch('/api/options/trades').then(r => r.json()).then(t => setTradeOptions(Array.isArray(t) ? t : [])).catch(console.error);
    fetch('/api/catalog/options/labor').then(r => r.json()).then(l => setLaborLineOptions(Array.isArray(l) ? l : [])).catch(console.error);
  }, []);

  // CO6 — catalog L1/L2/L3 taxonomy (from /api/catalog/tree) for the component
  // picker's cascading filter dropdowns. Loaded once; independent of the
  // product-tree (`categoryTree` above, which is /api/products/tree).
  const [catalogTaxonomy, setCatalogTaxonomy] = useState([]);
  useEffect(() => {
    fetch('/api/catalog/tree').then(r => r.json()).then(t => setCatalogTaxonomy(Array.isArray(t) ? t : [])).catch(console.error);
  }, []);
  useEffect(() => {
    reloadTree();
  }, [reloadTree]);

  // When tree loads (or reloads), refresh the category grid for the current selection (or root).
  useEffect(() => {
    if (selectedNodeId && /^c[123]-\d+$/.test(selectedNodeId)) {
      loadCatChildren(selectedNodeId, categoryTree);
    } else if (!selectedNodeId || !selectedNodeId.includes('|')) {
      // Root state — show unified L1 nodes
      setCatGridRows(
        categoryTree.map((n, i) => ({
          id: n._catId, nodeId: n.id, code: n.code, label: n.label,
          count: (n.children || []).filter(c => !c.isProduct).length,
          counts: n.counts || {},
          sort_order: n.sort_order ?? i, _pos: i + 1, _type: 'category',
        }))
      );
    }
    // Product nodes: leave catGridRows as-is
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryTree]);

  // flatFamilies is GONE with product_families (2026-07-29) — it flattened the
  // L1-grouped family list for dropdowns and breadcrumbs. Both now read the
  // category tree.

  // Init localValues from typical when a new product is selected.
  // Uses product.id in deps so re-loading the same product (after "set as typical")
  // does NOT reset localValues — the preview values stay in place.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Empty, not seeded with the typicals: the Simulate column shows blank until the
    // user actually tries something, and blank means "same as typical".
    setLocalValues({});
  }, [product?.id]);

  // Traverse categoryTree to find a node by id; returns path array from root to node.
  const findCatPath = useCallback((targetId, nodes = categoryTree, path = []) => {
    for (const n of nodes) {
      if (n.id === targetId) return [...path, n];
      if (n.children && !n.isProduct) {
        const found = findCatPath(targetId, n.children, [...path, n]);
        if (found) return found;
      }
    }
    return null;
  }, [categoryTree]);

  // Derive DataGrid rows for the selected category node (non-product children or products).
  // Includes _pos, _type, sort_order, and ancestor IDs for the path dropdowns.
  const loadCatChildren = useCallback((nodeId, tree = categoryTree) => {
    const find = (nid, nodes) => {
      for (const n of nodes) {
        if (n.id === nid) return n;
        if (n.children && !n.isProduct) { const r = find(nid, n.children); if (r) return r; }
      }
      return null;
    };
    const node = find(nodeId, tree);
    if (!node) { setCatGridRows([]); return; }
    const isL3 = nodeId.startsWith('c3-');
    const path = findCatPath(nodeId, tree) || [];

    if (isL3) {
      const products = (node.children || []).filter(c => c.isProduct);
      const l3Id = node._catId;
      const l2Id = path.length >= 2 ? path[path.length - 2]._catId : null;
      const l1Id = path.length >= 1 ? path[0]._catId : null;
      setCatGridRows(products.map((p, i) => ({
        id: p._productId, nodeId: p.id, code: p.code, label: p.label,
        status: p.status || 'draft', sort_order: p.sort_order ?? i,
        onshape_ref: p.onshape_ref || null,
        error_count: p.error_count || 0,
        _ownerOnly: p._ownerOnly || null,
        owner_project_id: p._ownerProjectId ?? null,
        _pos: i + 1, _type: 'product', _l3Id: l3Id, _l2Id: l2Id, _l1Id: l1Id,
      })));
    } else {
      const subs = (node.children || []).filter(c => !c.isProduct);
      const isL1 = nodeId.startsWith('c1-');
      const currentCatId = node._catId;
      const l1Id = isL1 ? currentCatId : (path.length >= 1 ? path[0]._catId : null);
      const l2Id = nodeId.startsWith('c2-') ? currentCatId : null;
      setCatGridRows(subs.map((c, i) => ({
        id: c._catId, nodeId: c.id, code: c.code, label: c.label,
        count: (c.children || []).filter(x => !x.isProduct).length,
        // R6: include counts for StatusCounts; flatten product-only leaf counts
        counts: c.counts || {},
        sort_order: c.sort_order ?? i,
        _pos: i + 1, _type: 'category', _l1Id: l1Id, _l2Id: l2Id,
      })));
    }
  }, [categoryTree]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load product when a leaf node is selected (no /cost call — resolver is client-side)
  const handleSelectNode = useCallback((id) => {
    onSelectNode?.(id ?? null);
    setProductFormMode(null);
    setCatFormOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setEditingHierarchyRowId(null); // R2: clear edit mode on navigation
    setSelectedHierarchyRowId(null); // §1: clear selection on navigation
    setEditingProductInfoRowId(null); // G1: clear product-info edit on navigation
    setEditingParamRowId(null);

    // null = root selection (from TreeNav title header)
    if (!id) {
      setProduct(null);
      setCatGridRows(
        categoryTree.map((n, i) => ({
          id: n._catId, nodeId: n.id, code: n.code, label: n.label,
          count: (n.children || []).filter(c => !c.isProduct).length,
          counts: n.counts || {},
          sort_order: n.sort_order ?? i, _pos: i + 1, _type: 'category',
        }))
      );
      return;
    }

    // Category node (c1-*, c2-*, c3-*) — no pipe in the cat portion
    if (/^c[123]-\d+$/.test(id)) {
      setProduct(null);
      loadCatChildren(id);
      return;
    }

    // Product leaf — c3-<catId>|<productId>
    const parts = id.split('|');
    if (parts.length < 2) {
      setProduct(null);
      return;
    }
    const productId = parts[1];
    setLoading(true);
    setProduct(null);
    fetch(`/api/products/${productId}`)
      .then(r => r.json())
      .then(p => {
        setProduct(p);
        setLoading(false);
        // Sync the tree + breadcrumb to the product's REAL location when the caller
        // passed a synthetic/unknown category — where-used and sub-product jumps use
        // "c3-0|<id>", which leaves the tree unselected and the title/breadcrumb empty.
        // Rebuild the node id from the fetched category_id so the tree highlights the
        // right node and the title resolves (mount effect is []-deps → no re-fetch loop).
        if (p && p.category_id != null) {
          const realId = `c3-${p.category_id}|${productId}`;
          if (realId !== id) onSelectNode?.(realId);
        }
      })
      .catch(err => { console.error(err); setLoading(false); });
  }, [onSelectNode, loadCatChildren, categoryTree]);

  // On mount: if App already has a saved selectedNodeId (user returning to tab), auto-load the product.
  // Graceful fallback: if product no longer exists, stay at family/category level.
  useEffect(() => {
    if (selectedNodeId && selectedNodeId.includes('|')) {
      const productId = selectedNodeId.split('|')[1];
      setLoading(true);
      fetch(`/api/products/${productId}`)
        .then(r => r.ok ? r.json() : null)
        .then(p => { if (p) setProduct(p); setLoading(false); })
        .catch(err => { console.error(err); setLoading(false); });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps — mount-only: restore prior selection

  // "Where used" — on-demand (Alex's request: lists can be long). Fetch only on
  // the user clicking "Load where-used"; cache per product until the selection
  // changes. Reset the cache whenever the selected product changes.
  useEffect(() => {
    setUsage(null);
    setUsageLoading(false);
  }, [product?.id]);

  // Phase 1c — reset the Onshape panel's local UI state when the selected
  // product changes (thumbnail/viewer/sync state must not leak across products).
  const loadUsage = useCallback(() => {
    const pid = product?.id;
    if (!pid || usageLoading) return;
    setUsageLoading(true);
    fetch(`/api/products/${pid}/usage`)
      .then(r => r.ok ? r.json() : { placements: [], components: [] })
      .then(u => { setUsage(u); setUsageLoading(false); })
      .catch(err => { console.error(err); setUsage({ placements: [], components: [] }); setUsageLoading(false); });
  }, [product?.id, usageLoading]);

  // Phase 1c — manual mass-properties sync. refresh=1: the explicit Sync click
  // IS the one live-call-permitted user action (per the Onshape quota discipline).
  const handleParamChange = useCallback((name, val) => {
    setLocalValues(prev => ({ ...prev, [name]: val }));
  }, []);

  // Reset the SIMULATE column — clear it, so every parameter reads at its typical
  // again. Empty is the resting state: resolveComps already falls back to
  // typical_value for any name localValues does not carry.
  const handleReset = useCallback(() => setLocalValues({}), []);
  const simulating = Object.values(localValues).some(v => v != null && v !== '');

  // Live-resolved grid rows from localValues (definition preview).
  const liveRows = useMemo(() => {
    if (!product) return [];
    return resolveComps(product.comps, product.params, localValues, product._labor_rate ?? product.trade ?? null, product._labor_productivity);
  }, [product, localValues]);

  // _seq is display-only; not stored in DB. _parent_param_names (Q8 v2): the
  // product's own param names, for the "⚠ <Name> not linked" nudge on a
  // param_map comp row (MappingFeedback in product-cols.jsx) — cheap to compute
  // once here rather than threading `params` through the grid column defs.
  const parentParamNames = useMemo(() => (product?.params || []).map(p => p.name), [product]);

  // S4 — Config chip: the server only embeds a comp's _sub_param_names when the
  // comp ALREADY has a param_map (product-db.js, Q8 v2 — it skips the sub-product
  // params query otherwise). A kind='product' comp with an empty/no param_map has
  // no server-side _sub_param_names, so the grid can't tell "⚑ M" (typical count)
  // or run the ⚠ same-name check without it. Reuse the exact GET the edit form
  // already uses (CompEditSubRow's subParamsByRef, above) once per distinct ref_id,
  // grid-wide, cached for the component's lifetime — no cost/Onshape call, no
  // resolver/DB write, just the same product read. subParamsRequestedRef guards
  // against re-fetching an id already in flight/resolved.
  const [gridSubParamsCache, setGridSubParamsCache] = useState({});
  const subParamsRequestedRef = useRef(new Set());
  useEffect(() => {
    const comps = product?.comps || [];
    const toFetch = comps.filter(c =>
      c.kind === 'product' && c.ref_id && !c._sub_param_names && !subParamsRequestedRef.current.has(c.ref_id)
    );
    if (toFetch.length === 0) return;
    let cancelled = false;
    toFetch.forEach(c => {
      subParamsRequestedRef.current.add(c.ref_id);
      fetch(`/api/products/${c.ref_id}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (!cancelled) setGridSubParamsCache(prev => ({ ...prev, [c.ref_id]: data?.params || null })); })
        .catch(() => { if (!cancelled) setGridSubParamsCache(prev => ({ ...prev, [c.ref_id]: null })); });
    });
    return () => { cancelled = true; };
  }, [product]);

  const gridRows = useMemo(() => {
    return liveRows.map((r, i) => ({
      ...r,
      _seq: i + 1,
      _parent_param_names: parentParamNames,
      // null/undefined = unavailable (grid shows "⚑ ?"); array (incl. empty) = known.
      _sub_param_names: r.kind === 'product' ? (r._sub_param_names ?? gridSubParamsCache[r.ref_id] ?? null) : r._sub_param_names,
    }));
  }, [liveRows, parentParamNames, gridSubParamsCache]);

  // Cost rollup — live sum of the definition preview.
  const { totalCost, totalHours, costLines } = useMemo(() => {
    const total = Math.round(
      gridRows.reduce((s, r) => s + (r.line_cost ?? 0), 0) * 100
    ) / 100;
    const hoursRows = gridRows.filter(r => r.hours != null);
    const hours = hoursRows.length > 0
      ? Math.round(hoursRows.reduce((s, r) => s + r.hours, 0) * 10000) / 10000
      : null;
    return { totalCost: total, totalHours: hours, costLines: gridRows.filter(r => r.line_cost != null) };
  }, [gridRows]);

  // HOURS BUT NO LABOUR (Alex's to-do, 2026-10-12). Install hours the components
  // carry but nothing charges, because no labour line resolves (none on the product,
  // none inherited from its category, or one with no rate). Summed from the LIVE
  // grid, so a Simulate value moves it too. Feeds the Components notice and the
  // header's Labour cell; the tree's ⚠ chip gets the same fact from the server.
  const unchargedHours = useMemo(() => {
    const h = gridRows.reduce((s, r) => s + (r.uncharged_hours > 0 ? r.uncharged_hours : 0), 0);
    return h > 0 ? Math.round(h * 10000) / 10000 : 0;
  }, [gridRows]);

  // CO1 — a component ref of kind='product' opens that sub-product in this same
  // Products screen, reusing the existing node-selection path (handleSelectNode).
  // No category ancestor is known for a cross-product jump, so a synthetic
  // "c3-0|<id>" node id is used: handleSelectNode only reads the id after '|' to
  // fetch the product; the (nonexistent) "c3-0" prefix just yields an empty
  // breadcrumb ancestor path instead of throwing.
  const onNavigateToProduct = useCallback((productId) => {
    handleSelectNode(`c3-0|${productId}`);
  }, [handleSelectNode]);

  // Q8 v2 — "show mappings" header toggle: expands the full linked-pairs detail line
  // for ALL param_map comp rows at once (the chip + ⚠ nudge always show regardless).

  // Grid columns — definition-only now (placement workspace moved to the Project screen).
  // laborInfo: the product's resolved labour line + rate, so a component row can
  // NAME the labour it charges instead of leaving $/Hr blank (Alex, 2026-07-19).
  const laborInfo = useMemo(
    () => (product ? { label: product._labor_label, rate: product._labor_rate, source: product._labor_source } : null),
    [product],
  );
  const gridColumns = useMemo(() => makeGridCols('definition', onNavigateToCatalog, onNavigateToProduct, laborInfo), [onNavigateToCatalog, onNavigateToProduct, laborInfo]);
  const tradeSelectOptions = useMemo(() => [
    { value: '', label: '— none —' },
    ...tradeOptions.map(t => ({ value: t.id, label: t.label })),
  ], [tradeOptions]);
  // The product header now picks a LABOUR LINE, not a trade (Alex, 2026-07-19),
  // through the SAME LevelCascade as the catalog's FabLabour cell (2026-08-03) —
  // so the raw option rows go straight to the column, with no flat {value,label}
  // shaping in between. The rate is shown by the cascade's leaf level.
  // productInfoCols moved BELOW handleMoveRow (§5 header row needs the move
  // cascade, and useMemo deps can't reference a later const — same TDZ note as
  // hierarchyCols).
  // Onshape-managed params (_mass_kg/_surface_m2, written by Sync) get the
  // ONSHAPE MARK on the Name so it's obvious the value comes from CAD — a manual
  // edit survives only until the next Sync overwrites it (tooltip says so).
  //
  // Two rejected predecessors, both for reasons worth keeping (NIGHT_SHIFT 7b):
  //   `⟳` collided with RESTORE (`⟲`) — they differed ONLY by arrow rotation,
  //        which breaks "shape = meaning" (Alex is colour-blind).
  //   `⚙`  fixed that but read as SETTINGS — and this marker sits on rows inside
  //        the CONFIGURATION section, where every row already IS a setting.
  // The Onshape mark itself says exactly the one thing meant: this came from
  // Onshape. It inherits `currentColor`, so the navy below still applies.
  const ONSHAPE_SYNCED_PARAMS = useMemo(() => new Set(['_mass_kg', '_surface_m2']), []);
  // Latest-handler ref for the list-param Typical cascade (below): handleParamCellSave
  // is defined AFTER paramCols, so it can't be a memo dep (TDZ) — the stable ref keeps
  // the cascade calling the current handler without staleness or a reorder.
  const paramCellSaveRef = useRef(null);

  const paramCols = useMemo(() => makeParamCols(unitOptions, sizeUnitSelectOptions, sizeListValuesCache).map(col => {
    if (col.key === 'name') {
      return {
        ...col,
        render: row => ONSHAPE_SYNCED_PARAMS.has(row.name) ? (
          <span
            title={`Managed by Onshape Sync${product?.onshape_synced_at ? ` — last synced ${product.onshape_synced_at}` : ''}. Manual edits are overwritten on the next Sync.`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <OnshapeMark size={12} style={{ color: 'var(--th-navy)', flexShrink: 0 }} />
            <span>{row.name}</span>
          </span>
        ) : row.name,
      };
    }
    // Size-picker consistency (Spike, 2026-07-13): a kind='list' param's Typical edits
    // via the SAME CascadeDropdown as the catalog Size cell + the component picker —
    // via a custom render gated on edit mode (editingParamRowId), NOT the built-in
    // editType:'select' path. Other kinds (boolean checkbox / numeric) are untouched:
    // we only drop 'list' from the row-aware editType so DataGrid doesn't ALSO render
    // its select editor over the cascade. Read mode + boolean/number keep col.render.
    if (col.key === 'typical_value') {
      const baseRender = col.render;
      return {
        ...col,
        // Drive every kind through the row-aware editType and turn OFF the static
        // `editable` flag: otherwise a 'list' row (editType undefined) would still hit
        // DataGrid's `col.editable` branch and render a TextCell over the cascade.
        // boolean→checkbox, count/magnitude→text (unchanged), list→render-only (cascade).
        editable: false,
        editType: row => row.type === 'boolean' ? 'checkbox' : row.type === 'list' ? undefined : 'text',
        render: row => {
          const inEdit = String(row.id) === String(editingParamRowId);
          if (row.type === 'list' && inEdit) {
            const opts = resolveListOptions(row, sizeListValuesCache).map(o => ({ code: o.value, display: o.label, label: '' }));
            return (
              <CascadeDropdown
                ariaLabel="Typical"
                width={130}
                allowClear={false}
                placeholder="— pick —"
                value={row.typical_text || ''}
                options={opts}
                onChange={v => paramCellSaveRef.current?.(row.id, 'typical_value', v)}
              />
            );
          }
          return baseRender(row);
        },
      };
    }
    return col;
  }).concat([{
    // SIMULATE (Alex, 2026-08-02) — a scratch value for trying a number WITHOUT
    // touching the typical. This replaces the right-hand "Configuration — Definition"
    // panel, which he never used: *"I am not using it, I am directly using the
    // parameters. Issue is that I need to go back to the typical I would like to
    // have."* Editing Typical to test something destroyed the value he wanted to keep.
    //
    // So the two are separated by column, not by mode. Typical is the STORED value and
    // saves as it always did; Simulate is never written anywhere — it only feeds the
    // live cost preview (liveRows -> resolveComps), and Reset drops it back to typical.
    // Blank means "same as typical", so an untouched grid reads clean.
    key: '_simulate', label: 'Simulate', width: PARAM_SIMULATE_W, align: 'right',
    render: (row) => {
      // An internal param is not simulatable — it is either a constant or computes
      // itself, so there is nothing to try. It is still SHOWN, read-only, with the
      // value in force: when you are chasing a number, the derived ones are usually
      // the number you are chasing. Hiding them would just move the confusion.
      if (row.configurable != null && !Number(row.configurable)) {
        const shown = liveParamValues?.[row.name];
        return (
          <span title="Internal — not configurable, so not simulatable"
                style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', color: 'var(--th-text-secondary)' }}>
            {shown == null ? '—' : (Math.round(shown * 1e6) / 1e6)}
          </span>
        );
      }
      const sim = localValues[row.name];
      const differs = sim != null && sim !== '' && Number(sim) !== Number(row.typical_value);
      // A SIZE parameter must simulate from its list, not from a free number box
      // (Alex, 2026-08-02) — the same control the Typical cell and the placement
      // expander use. The stored value stays the NUMERIC nps, which is what the
      // resolver env expects and what typical_value holds for a list param; a custom
      // list has no numeric axis, so its own option value goes through as-is.
      if (row.type === 'list') {
        const opts = resolveListOptions(row, sizeListValuesCache);
        const isSizeUnit = !!row.size_unit && row.size_unit !== 'custom';
        const current = sim ?? row.typical_value;
        return (
          <SelectCell
            value={String(current ?? '')}
            options={opts.map(o => ({ value: String(isSizeUnit ? o.num : o.value), label: o.label }))}
            title="Simulate only — never saved"
            onChange={raw => handleParamChange(row.name, isSizeUnit ? Number(raw) : raw)}
          />
        );
      }
      if (row.type === 'boolean') {
        return (
          <input
            type="checkbox"
            checked={Number(sim ?? row.typical_value) === 1}
            onChange={e => handleParamChange(row.name, e.target.checked ? 1 : 0)}
            onClick={e => e.stopPropagation()}
            title="Simulate only — never saved"
          />
        );
      }
      return (
        <input
          type="number"
          value={sim ?? ''}
          placeholder={row.typical_value ?? ''}
          onChange={e => handleParamChange(row.name, e.target.value === '' ? undefined : Number(e.target.value))}
          onClick={e => e.stopPropagation()}
          title="Simulate only — never saved. Blank = same as typical."
          style={{
            width: '100%', boxSizing: 'border-box', textAlign: 'right', padding: 'var(--th-cell-inset, 3px 7px)', borderRadius: 3,
            lineHeight: 'var(--th-cell-line-h, 1.15)',
            fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)',
            fontVariantNumeric: 'tabular-nums',
            border: `1px solid ${differs ? 'var(--th-warn-accent)' : 'var(--th-border-subtle)'}`,
            background: 'var(--th-row-even)',
            fontWeight: differs ? 700 : 400,
          }}
        />
      );
    },
  }]), [unitOptions, sizeUnitSelectOptions, sizeListValuesCache, product?.onshape_synced_at, ONSHAPE_SYNCED_PARAMS, editingParamRowId, localValues, liveParamValues, handleParamChange]);

  // -- Product authoring (BUILD 1) -------------------------------------------

  const handleOpenCreateProduct = useCallback(() => {
    if (/^c3-\d+$/.test(selectedNodeId)) {
      const catId = parseInt(selectedNodeId.slice(3), 10);
      setProductFormFields({ code: '', category_id: catId, label: '', placeable: false, status: 'draft', trade: '' });
    } else {
      // No category selected — a product must be created inside an L3 category.
      setProductFormFields({ code: '', category_id: null, label: '', placeable: false, status: 'draft', trade: '' });
    }
    setProductFormMode('create');
  }, [selectedNodeId]);

  const handleOpenEditProduct = useCallback(() => {
    if (!product) return;
    setProductFormFields({
      code: product.code,
      label: product.label,
      placeable: !!product.placeable,
      status: product.status,
      trade: product.trade || '',
    });
    setProductFormMode('edit');
  }, [product]);

  const handleSaveProduct = async () => {
    if (savingProduct) return;
    const { code, category_id, label, placeable, status, trade } = productFormFields;
    if (!code.trim() || !label.trim()) { setActionError('Code and Label are required.'); return; }
    // A product lives in an L3 category. The old "family OR category" fallback
    // went with product_families (2026-07-29).
    if (!category_id) { setActionError('Select an L3 category first.'); return; }
    setSavingProduct(true);
    try {
      const body = {
        code: code.trim(), label: label.trim(), placeable: placeable ? 1 : 0, status, trade: trade || null,
        category_id: Number(category_id),
      };
      const url = productFormMode === 'create' ? '/api/products' : `/api/products/${product.id}`;
      const res = await fetch(url, { method: productFormMode === 'create' ? 'POST' : 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) { const e = await res.json(); setActionError(e.error); return; }
      const saved = await res.json();
      reloadTree();
      setProductFormMode(null);
      const nodeId = `c3-${category_id}|${saved.id}`;
      handleSelectNode(nodeId);
    } catch (err) { console.error(err); } finally { setSavingProduct(false); }
  };

  const handleProductCellSave = useCallback(async (rowId, field, value) => {
    let payload;
    if (field === 'placeable') payload = { placeable: Number(value) };
    else if (field === 'trade') payload = { trade: value || null };
    else payload = { [field]: value };
    const res = await fetch(`/api/products/${rowId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      // ERR-1(b) — THROW. DataGrid catches it and renders the reason under this row,
      // keeping the row in edit mode so the rejected value can be corrected. This is the
      // path that refused Qty=Length with a good sentence Alex never saw (2026-08-04),
      // because the grid sat far below the page-level Notice.
      //
      // ⚠ There is deliberately no try/catch around this any more. The previous one
      // swallowed every failure into console.error, which is the same bug one layer down:
      // a caught-and-logged error is invisible to the person who caused it.
      throw new Error(e.error || 'Save failed');
    }
    const fresh = await fetch(`/api/products/${rowId}`).then(r => r.json());
    setProduct(fresh);
  }, []);

  const handleParamCellSave = useCallback(async (rowId, field, value) => {
    const nullableNum = v => (v === '' || v == null) ? null : Number(v);
    // S1b: the Typical column's grid key stays 'typical_value' for every kind (one
    // column, per S1a/S1b's row-aware editType), but a 'list' param's picked option is
    // a STRING stored in typical_text (typical_value is REAL, holds a 0 placeholder for
    // list rows — see product-db.js). Translate here so the server gets the right field.
    const row = (product?.params || []).find(p => String(p.id) === String(rowId));
    // Same translation, second case (2026-08-03): on an INTERNAL param the Typical
    // cell edits the FORMULA, so the staged value lands in value_expr instead. The
    // grid keeps one column key; this decides the field, exactly as it does for a
    // list param's typical_text.
    const rowInternal = row && row.configurable != null && !Number(row.configurable);
    const payload = (field === 'typical_value' && rowInternal)
      ? { value_expr: (value === '' || value == null) ? null : String(value) }
      : (field === 'typical_value' && row?.type === 'list')
      ? { typical_text: value || null }
      : field === 'configurable'
        ? { configurable: Number(value) ? 1 : 0 }
        : ['typical_value'].includes(field)
        ? { [field]: Number(value) }
        : ['min', 'max'].includes(field)
          ? { [field]: nullableNum(value) }
          : { [field]: value };
      const res = await fetch(`/api/products/params/${rowId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        // ERR-1(b) — THROW; DataGrid places it beside the parameter it concerns. A
        // naming-rule rejection names a field, and reading it hundreds of pixels from that
        // field is most of the work. Note this used to continue past the failure and
        // refetch anyway, so a rejected edit still looked like it had done something.
        throw new Error(e.error || 'Save failed');
      }
      const fresh = await fetch(`/api/products/${product.id}`).then(r => r.json());
      setProduct(fresh);
      // Keep the definition preview (localValues, seeded from the typicals at load) in
      // sync with a saved Typical — otherwise the stale seeded value shadows the new
      // typical in resolveComps (localValues[name] ?? typical_value) and the component
      // list wouldn't re-cost. Params are the plan of record → editing a Typical must
      // re-cost the list. (For a list param, typical_value carries the numeric nps.)
      if (field === 'typical_value') {
        const fr = (fresh?.params || []).find(p => String(p.id) === String(rowId));
        if (fr) setLocalValues(prev => ({ ...prev, [fr.name]: fr.typical_value }));
      }
      recheckConfigFromCache(product.id);
  }, [product, recheckConfigFromCache]);
  // Keep the list-param Typical cascade (paramCols, above) pointed at the current
  // handleParamCellSave without making it a memo dep (it's declared after paramCols).
  paramCellSaveRef.current = handleParamCellSave;

  // STANDARD (Alex 2026-07-02): guard runs on ✕ BEFORE the confirm. Client-side
  // mirror of the server's paramUsedBy (word-boundary match over this product's
  // comp formulas) — the server guard in deleteProductParam stays the authority.
  const handleParamDeleteCheck = useCallback((row) => {
    const re = new RegExp(`\\b${String(row.name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`);
    const hits = (product?.comps || []).filter(
      c => (c.qty_expr && re.test(c.qty_expr)) || (c.size_expr && re.test(c.size_expr))
    );
    if (hits.length > 0) return `Parameter "${row.name}" is used by ${hits.length} component formula(s) — update those first.`;
    return null;
  }, [product]);

  const handleDeleteParam = useCallback(async (row) => {
    const res = await fetch(`/api/products/params/${row.id}`, { method: 'DELETE' });
    if (!res.ok) {
      // C6: throw so the DataGrid's WS1a-i inline delete-warning renders the
      // guard message in the grid (no alert/confirm popup).
      const e = await res.json();
      throw new Error(e.error || 'Could not delete this parameter.');
    }
    const fresh = await fetch(`/api/products/${product.id}`).then(r => r.json());
    setProduct(fresh);
    setLocalValues(prev => { const n = { ...prev }; delete n[row.name]; return n; });
    recheckConfigFromCache(product.id);
  }, [product?.id, recheckConfigFromCache]);

  // WS1b — G3: duplicate a config param (server appends " copy" to the name).
  const handleCopyParam = useCallback(async (row) => {
      const res = await fetch(`/api/products/params/${row.id}/copy`, { method: 'POST' });
      // Threw this into console.error before, which is invisible to whoever pressed the
      // button. DataGrid places it under the row now.
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Copy failed'); }
      const fresh = await fetch(`/api/products/${product.id}`).then(r => r.json());
      setProduct(fresh);
  }, [product?.id]);

  const handleAddParam = async () => {
    const { name, type, typical_value, unit, min, max, description, size_unit, customOptions, typical_text, configurable, value_expr } = newParam;
    const isInternalNew = !Number(configurable);
    const exprNew = isInternalNew && String(value_expr || '').trim() !== '' ? String(value_expr).trim() : null;
    if (!name.trim()) { setActionError('Name is required.'); return false; }
    // An INTERNAL param may compute itself instead of holding a value, so a formula
    // satisfies "typical required" — typical_value then rides along as the 0 placeholder
    // the NOT NULL column needs, exactly as it does for boolean and list.
    // S1b: a list param's typical is a picked option (typical_text), not a number —
    // typical_value stays '0' (set when Type switched to List) so this check still
    // passes; the real requirement below is size_unit.
    if (type !== 'list' && !exprNew && (typical_value === '' || isNaN(Number(typical_value)))) { setActionError('Typical value (number) is required — or untick Config? and give it a formula.'); return false; }
    if (type === 'list' && !size_unit) { setActionError('Size Unit is required for a List param.'); return false; }
    setSavingParam(true);
    try {
      // S1a: Unit/Min/Max are N/A for a Y/N param — blank them client-side too (the
      // server also force-clears them for kind='boolean', belt-and-braces). S1b: same
      // N/A treatment for 'list'; its own size_unit/options/typical_text go along.
      const isBool = type === 'boolean';
      const isList = type === 'list';
      const res = await fetch(`/api/products/${product.id}/params`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(), type,
          typical_value: exprNew ? 0 : Number(typical_value),
          configurable: isInternalNew ? 0 : 1,
          value_expr: exprNew,
          unit: (isBool || isList) ? null : (unit || null),
          min: (isBool || isList) ? null : (min !== '' ? Number(min) : null),
          max: (isBool || isList) ? null : (max !== '' ? Number(max) : null),
          description: description || null,
          ...(isList ? {
            size_unit,
            options: size_unit === 'custom' ? customOptions : null,
            typical_text: typical_text || null,
          } : {}),
        }),
      });
      if (!res.ok) { const e = await res.json(); setActionError(e.error); return false; }
      const fresh = await fetch(`/api/products/${product.id}`).then(r => r.json());
      setProduct(fresh);
      setNewParam(NEW_PARAM_INIT);
      setNewOptionDraft({ value: '', label: '' });
      recheckConfigFromCache(product.id);
      // Returns the add-success boolean so the ghost row closes only on an ACTUAL
      // add -- the same contract the component add row uses.
      return true;
    } catch (err) { console.error(err); return false; } finally { setSavingParam(false); }
  };

  // ── ADD PARAMETER, IN THE GRID (2026-08-04) ────────────────────────────────
  // One editor per column key, rendered in that column by DataGrid's S3e in-cell
  // add lane. Every branch below is the SAME rule the row editor uses, because the
  // complaint was precisely that the two disagreed: internal → Typical is a formula
  // box; boolean/list → Unit/Min/Max read NA; list → Size Unit appears and Typical
  // becomes its option cascade.
  // The add row is a cascade too: Type -> (Size Unit) -> Typical. Auto-advance from
  // the shared hook, so this new surface is born compliant instead of joining the
  // seven that were not (UX-3).
  const addParamCascade = useCascadeSignal();
  const setP = patch => setNewParam(p => ({ ...p, ...patch }));
  const npInternal = !Number(newParam.configurable);
  const npNA = newParam.type === 'boolean' || newParam.type === 'list';
  const NA_ADD = <Text kind="empty">NA</Text>;
  const paramAddInlineEditRender = {
    name:        () => <TextCell value={newParam.name} onChange={v => setP({ name: v })} placeholder="e.g. FlowRate" />,
    description: () => <TextCell value={newParam.description} onChange={v => setP({ description: v })} placeholder="optional note" />,
    configurable: () => (
      <CheckboxCell
        value={newParam.configurable}
        label={['Yes', 'internal']}
        title="Ticked = a knob: broadcast when this product is placed or embedded. Unticked = internal plumbing, and free to carry a formula in Typical."
        onChange={b => setP({ configurable: b ? '1' : '0', ...(b ? { value_expr: '' } : {}) })}
      />
    ),
    type: () => (
      <SelectCell
        value={newParam.type}
        options={[{ value: 'count', label: 'count' }, { value: 'magnitude', label: 'magnitude' }, { value: 'boolean', label: 'Y/N' }, { value: 'list', label: 'List' }]}
        onChange={type => {
          setP(type === 'boolean' || type === 'list'
            ? { type, typical_value: '0', unit: '', min: '', max: '', size_unit: '', customOptions: [], typical_text: '' }
            : { type, typical_value: npNA ? '' : newParam.typical_value, size_unit: '', customOptions: [], typical_text: '' });
          if (type === 'list') addParamCascade.advance('size_unit');
        }}
      />
    ),
    size_unit: () => (newParam.type !== 'list' ? NA_ADD : (
      <CascadeDropdown
        ariaLabel="Size Unit" width={110} placeholder="— choose —"
        value={newParam.size_unit}
        options={sizeUnitSelectOptions.map(o => ({ code: o.value, label: o.label }))}
        openSignal={addParamCascade.signalFor('size_unit')}
        onChange={size_unit => {
          setP({ size_unit, customOptions: [], typical_text: '' });
          if (size_unit && size_unit !== 'custom') fetchSizeListValues(size_unit);
          // 'custom' has no options yet -- its list is built in the sub-row below,
          // so opening an empty Typical picker would be a dead end.
          if (size_unit && size_unit !== 'custom') addParamCascade.advance('typical');
        }}
      />
    )),
    typical_value: () => {
      if (npInternal) return <FormulaCell value={newParam.value_expr} onChange={v => setP({ value_expr: v })} placeholder="= Mass * 1.2" />;
      if (newParam.type === 'boolean') return <CheckboxCell value={newParam.typical_value} onChange={b => setP({ typical_value: b ? '1' : '0' })} />;
      if (newParam.type === 'list') {
        return (
          <CascadeDropdown
            ariaLabel="Typical" width={130} allowClear={false} placeholder="— choose —"
            value={newParam.typical_text}
            openSignal={addParamCascade.signalFor('typical')}
            options={(newParam.size_unit === 'custom' ? newParam.customOptions : (sizeListValuesCache[newParam.size_unit] || []))
              .map(o => ({ code: o.value, display: o.label, label: '' }))}
            onChange={typical_text => setP({ typical_text })}
          />
        );
      }
      return <TextCell type="number" align="right" value={newParam.typical_value} onChange={v => setP({ typical_value: v })} placeholder="0" />;
    },
    unit: () => (npNA ? NA_ADD : <TextCell value={newParam.unit} onChange={v => setP({ unit: v })} placeholder="e.g. SCFH" />),
    min:  () => (npNA ? NA_ADD : <TextCell type="number" align="right" value={newParam.min} onChange={v => setP({ min: v })} placeholder="—" />),
    max:  () => (npNA ? NA_ADD : <TextCell type="number" align="right" value={newParam.max} onChange={v => setP({ max: v })} placeholder="—" />),
  };

  // Add/Cancel BOTTOM-LEFT in the sub-row, the same shape and the same words as the
  // component add row — the other half of Alex's complaint. The custom-options editor
  // rides here too: it is a list-of-things, which no single cell can hold.
  const renderParamAddSubRow = (close) => (
    <div style={{ padding: '8px', background: 'var(--th-row-selected)', borderLeft: '3px solid var(--th-row-selected-border)' }}>
      {newParam.type === 'list' && newParam.size_unit === 'custom' && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 1, fontSize: 'var(--fs-small)', color: 'var(--th-text-body)' }}>
            Option value
            <input value={newOptionDraft.value} onChange={e => setNewOptionDraft(d => ({ ...d, value: e.target.value }))}
              placeholder="e.g. A" style={{ fontSize: 'var(--fs-small)', padding: '2px 5px', border: '1px solid var(--th-border-subtle)', borderRadius: 3, width: 70 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 1, fontSize: 'var(--fs-small)', color: 'var(--th-text-body)' }}>
            Option label
            <input value={newOptionDraft.label} onChange={e => setNewOptionDraft(d => ({ ...d, label: e.target.value }))}
              placeholder="e.g. Option A" style={{ fontSize: 'var(--fs-small)', padding: '2px 5px', border: '1px solid var(--th-border-subtle)', borderRadius: 3, width: 100 }} />
          </label>
          <button
            onClick={() => {
              if (!newOptionDraft.value.trim() || !newOptionDraft.label.trim()) return;
              setNewParam(p => ({ ...p, customOptions: [...p.customOptions, { value: newOptionDraft.value.trim(), label: newOptionDraft.label.trim() }] }));
              setNewOptionDraft({ value: '', label: '' });
            }}
            style={BTN_SECONDARY}
          >+ Add option</button>
          {newParam.customOptions.length > 0 && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {newParam.customOptions.map((o, i) => (
                // radius 3, not 10 — a pill/slot shape on a rectangle-only surface
                // (Alex, 2026-08-21: "not slot look, only rectangle").
                <span key={`${o.value}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 'var(--fs-small)', padding: '2px 6px', borderRadius: 3, background: 'var(--th-row-even)', border: '1px solid var(--th-border-subtle)' }}>
                  {o.label} <span style={{ color: 'var(--th-text-disabled)', fontFamily: 'var(--ff-mono)' }}>({o.value})</span>
                  <button
                    onClick={() => setNewParam(p => ({
                      ...p,
                      customOptions: p.customOptions.filter((_, idx) => idx !== i),
                      typical_text: p.typical_text === o.value ? '' : p.typical_text,
                    }))}
                    title="Remove option"
                    style={{ ...BTN_GLYPH, padding: 0, color: 'var(--th-text-disabled)', lineHeight: 1 }}
                  >✕</button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button onClick={async () => { const ok = await handleAddParam(); if (ok) close(); }} disabled={savingParam}
          style={{ ...BTN_PRIMARY, ...(savingParam ? BTN_DISABLED : null) }}
        >{savingParam ? '…' : 'Add'}</button>
        <button onClick={() => { setNewParam(NEW_PARAM_INIT); setNewOptionDraft({ value: '', label: '' }); close(); }} style={BTN_SECONDARY}>Cancel</button>
        <Text kind="note">
          Add parameter — Add to save, Cancel to discard.
        </Text>
      </div>
    </div>
  );

  // Alex, 2026-08-04: "Missing reorder in the parameter grid". sort_order landed on
  // 2026-08-03 and getProduct already orders by it -- there was simply no way to WRITE it.
  const handleReorderParam = useCallback(async (paramId, targetIndex) => {
    if (!product?.id) return;
    try {
      const res = await fetch(`/api/products/params/${paramId}/move`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ toIndex: targetIndex }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); setActionError(e.error || 'Reorder failed'); return; }
      const fresh = await fetch(`/api/products/${product.id}`).then(r => r.json());
      setProduct(fresh);
    } catch (err) { console.error(err); }
  }, [product?.id]);

  const handleReorderComp = useCallback(async (compId, targetIndex) => {
    if (!product?.id) return;
    try {
      const res = await fetch(`/api/products/comps/${compId}/move`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ toIndex: targetIndex }),
      });
      if (!res.ok) return;
      const fresh = await fetch(`/api/products/${product.id}`).then(r => r.json());
      setProduct(fresh);
    } catch (err) { console.error(err); }
  }, [product?.id]);

  // -- Component authoring (BUILD 2) -----------------------------------------

  // handleCompSearch(q, undefined, filters) → run a catalog-line search for q
  //   (+ the CALLER's current l1/l2/l3 filters — v28: the filter state now
  //   lives inside CompEditSubRow, so it's passed in per-call, not read from
  //   an outer ProductsView state).
  // handleCompSearch(label, {keepQuery})    → a result was picked: show the
  //   label as the query text and clear the results dropdown (no new fetch).
  // CO6 BROWSE MODE: with l1/l2/l3 filters active, an empty/short q still
  // searches (server lists the category's lines); the ≥2-char rule only
  // gates the text-only, no-filter path.
  const runCompSearch = useCallback(async (q, filters) => {
    const text = (q || '').trim();
    const hasFilter = Boolean(filters.l1 || filters.l2 || filters.l3);
    if (!hasFilter && text.length < 2) { setSearchResults([]); return; }
    try {
      const params = new URLSearchParams();
      if (text) params.set('q', text);
      if (filters.l1) params.set('l1', filters.l1);
      if (filters.l2) params.set('l2', filters.l2);
      if (filters.l3) params.set('l3', filters.l3);
      const res = await fetch(`/api/catalog/lines/search?${params.toString()}`).then(r => r.json());
      setSearchResults(Array.isArray(res) ? res : []);
    } catch { setSearchResults([]); }
  }, []);

  const handleCompSearch = useCallback((q, opts, filters) => {
    if (opts?.keepQuery) { setSearchQuery(q); setSearchResults([]); return; }
    setSearchQuery(q);
    runCompSearch(q, filters || { l1: '', l2: '', l3: '' });
  }, [runCompSearch]);

  // Add a component. `payload` comes from CompEditSubRow (add mode) already
  // shaped as { kind, ref_id, description, size_expr, qty_expr } — no label and
  // no size_mode since 2026-07-29; both are derived from the reference now.
  // The retired legacy quantity fields (base_qty/driver_param/unit_or_ratio/
  // factor) are intentionally omitted — the server supplies its defaults
  // (base_qty=1, driver_param=null, unit_or_ratio='unit', factor=1), which is
  // exactly what the old blank inputs sent (write-identical, WS0.1).
  // Returns true on success, false on any validation/server failure — so the
  // renderAddForm ghost-row (item 3) only closes after an ACTUAL add, matching
  // the old above-grid form's behavior (stayed open so the user could fix it).
  // DS-8 (2026-08-21) — THROWS on failure instead of setActionError. A component
  // add/edit has a row to sit under (the ghost row / the edited row's own sub-row),
  // so per this view's own ERR-1(b) rule (above, at actionError's declaration)
  // actionError was never the right home for it — that channel is for failures
  // with NO row to sit under. ComponentsGrid's own onSave/onAddSave wrappers catch
  // this and show it in that row, the same way DataGrid's own built-in edit bar
  // shows a thrown save error under ITS row.
  const handleAddComp = async (payload) => {
    // The name is DERIVED from the reference now (product_comps.label was dropped
    // 2026-07-29), so a payload legitimately has none — this used to check
    // payload.label and therefore refused EVERY add, even with a line picked.
    // The reference is the thing that is actually required, for both kinds.
    if (!payload.ref_id) {
      throw new Error(payload.kind === 'product'
        ? 'Pick a sub-product first.'
        : 'Pick a catalog line or family first.');
    }
    const res = await fetch(`/api/products/${product.id}/comps`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Could not add this component.'); }
    const fresh = await fetch(`/api/products/${product.id}`).then(r => r.json());
    setProduct(fresh);
    setSearchQuery('');
    setSearchResults([]);
    return true;
  };

  // Unified save handler for CompEditSubRow — routes to add (POST) or edit (PATCH).
  // Both now THROW on failure (DS-8) instead of swallowing it or resolving `true`
  // regardless — see handleAddComp/handleSaveEditComp.
  const handleCompFormSave = async (compId, fields) => {
    if (compId == null) return handleAddComp(fields);
    await handleSaveEditComp(compId, fields);
    return true;
  };

  const handleDeleteComp = useCallback(async (row) => {
    try {
      const res = await fetch(`/api/products/comps/${row.id}`, { method: 'DELETE' });
      if (!res.ok) { const e = await res.json(); console.error(e.error); return; }
      const fresh = await fetch(`/api/products/${product.id}`).then(r => r.json());
      setProduct(fresh);
      if (selectedCompId === row.id) setSelectedCompId(null);
    } catch (err) { console.error(err); }
  }, [product?.id, selectedCompId]);

  // WS1b — G2: duplicate a component within the same product (sort_order appended at end).
  const handleCopyComp = useCallback(async (row) => {
    try {
      const res = await fetch(`/api/products/comps/${row.id}/copy`, { method: 'POST' });
      if (!res.ok) { const e = await res.json(); console.error(e.error); return; }
      const fresh = await fetch(`/api/products/${product.id}`).then(r => r.json());
      setProduct(fresh);
    } catch (err) { console.error(err); }
  }, [product?.id]);

  // DS-8 — this used to never check res.ok at all: a rejected PATCH was silently
  // treated as success (fresh refetch + close), with only a console.error if the
  // FETCH ITSELF threw (offline, say) — a genuine bug, not a display-position
  // choice. Now throws on either failure, same as handleAddComp above.
  const handleSaveEditComp = useCallback(async (compId, fields) => {
    const res = await fetch(`/api/products/comps/${compId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Could not save this component.'); }
    const fresh = await fetch(`/api/products/${product.id}`).then(r => r.json());
    setProduct(fresh);
    setSelectedCompId(null);
  }, [product?.id]);

  const handleGoToRoot = useCallback(() => {
    onSelectNode?.(null);
    setProduct(null);
    setProductFormMode(null);
    setCatFormOpen(false); setSearchQuery(''); setSearchResults([]);
    setEditingHierarchyRowId(null);
    setCatGridRows(
      categoryTree.map((n, i) => ({
        id: n._catId, nodeId: n.id, code: n.code, label: n.label,
        count: (n.children || []).filter(c => !c.isProduct).length,
        counts: n.counts || {},
        sort_order: n.sort_order ?? i, _pos: i + 1, _type: 'category',
      }))
    );
  }, [onSelectNode, categoryTree]);

  // -- Category CRUD (ProductHierarchyGrid BUILD 2) ---------------------------

  const handleAddL1Category = useCallback(async (fields) => {
    if (!fields.code?.trim() || !fields.label?.trim()) throw new Error('Code and Label are required.');
    const res = await fetch('/api/products/categories', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ level: 1, code: fields.code.trim(), label: fields.label.trim() }),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    reloadTree();
  }, [reloadTree]);

  const handleAddCategory = useCallback(async (fields) => {
    if (!fields.code?.trim() || !fields.label?.trim()) throw new Error('Code and Label are required.');
    const level = selectedNodeId?.startsWith('c1-') ? 2 : selectedNodeId?.startsWith('c2-') ? 3 : null;
    if (!level) throw new Error('Select an L1 or L2 category node first.');
    const parentId = parseInt(selectedNodeId.slice(3), 10);
    const res = await fetch('/api/products/categories', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parent_id: parentId, level, code: fields.code.trim(), label: fields.label.trim() }),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    reloadTree();
  }, [selectedNodeId, reloadTree]);

  const handleSaveCatLabel = useCallback(async (rowId, field, value) => {
    const res = await fetch(`/api/products/categories/${rowId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: value }),
    });
    // THROW: DataGrid reads a quiet return as a successful save.
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Save failed'); }
    reloadTree();
  }, [reloadTree]);

  // Unified save handler for the hierarchy grid — routes by row._type.
  const handleSaveHierarchyRow = useCallback(async (rowId, field, value) => {
    const row = catGridRows.find(r => String(r.id) === String(rowId));
    if (row?._type === 'product') {
      const res = await fetch(`/api/products/${rowId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      // THROW: DataGrid reads a quiet return as a successful save.
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Save failed'); }
      reloadTree();
      setCatGridRows(prev => prev.map(r => String(r.id) === String(rowId) ? { ...r, [field]: value } : r));
    } else {
      await handleSaveCatLabel(rowId, field, value);
    }
  }, [catGridRows, handleSaveCatLabel, reloadTree]);

  // Add a product directly from the L3 grid's add-row form (Fix 1).
  const handleAddProductRow = useCallback(async (fields) => {
    if (!fields.code?.trim()) throw new Error('Tag (code) is required.');
    if (!fields.label?.trim()) throw new Error('Label is required.');
    const catId = parseInt(selectedNodeId.slice(3), 10);
    const res = await fetch('/api/products', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: fields.code.trim(), label: fields.label.trim(), status: 'draft', category_id: catId }),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    reloadTree(); // triggers the useEffect([categoryTree]) → loadCatChildren to refresh the grid
  }, [selectedNodeId, reloadTree]);

  const handleAddProductToFamily = useCallback(async (fields) => {
    if (!fields.code?.trim()) throw new Error('Tag (code) is required.');
    if (!fields.label?.trim()) throw new Error('Label is required.');
    const familyId = parseInt(selectedNodeId, 10);
    const res = await fetch('/api/products', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: fields.code.trim(), label: fields.label.trim(), status: 'draft', family_id: familyId }),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    const newProd = await res.json();
    setCatGridRows(prev => [...prev, {
      id: String(newProd.id), nodeId: `${familyId}|${newProd.id}`,
      code: newProd.code, label: newProd.label, status: newProd.status || 'draft',
    }]);
  }, [selectedNodeId]);

  const handleDeleteCategory = useCallback(async (row) => {
    const res = await fetch(`/api/products/categories/${row.id}`, { method: 'DELETE' });
    if (!res.ok) { const e = await res.json(); setActionError(e.error); return; }
    reloadTree();
    setCatGridRows(prev => prev.filter(r => r.id !== row.id));
  }, [reloadTree]);

  const handleDeleteProductFromGrid = useCallback(async (row) => {
    const res = await fetch(`/api/products/${row.id}`, { method: 'DELETE' });
    if (!res.ok) { const e = await res.json(); setActionError(e.error || 'Delete failed'); return; }
    reloadTree();
    setCatGridRows(prev => prev.filter(r => r.id !== row.id));
  }, [reloadTree]);

  // Unified delete — routes by row._type
  const handleDeleteHierarchyRow = useCallback(async (row) => {
    if (row._type === 'product') {
      await handleDeleteProductFromGrid(row);
    } else {
      await handleDeleteCategory(row);
    }
  }, [handleDeleteProductFromGrid, handleDeleteCategory]);

  // WS1b — G1: clone a product (definition only: comps + params, no placements).
  // Only meaningful for product rows in the hierarchy grid; categories are skipped.
  const handleCopyHierarchyRow = useCallback(async (row) => {
    if (row._type !== 'product') return;
    const res = await fetch(`/api/products/${row.id}/copy`, { method: 'POST' });
    if (!res.ok) { const e = await res.json(); setActionError(e.error); return; }
    reloadTree();
  }, [reloadTree]);

  // R5 + STANDARD (Alex 2026-07-02): the guard runs on ✕ BEFORE the confirm.
  // Categories: sync count check. Products: async usage check (placements /
  // used-as-component) mirroring the server's deleteProduct guards.
  const handleDeleteCheck = useCallback((row) => {
    if (row._type === 'product') {
      return fetch(`/api/products/${row.id}/usage`)
        .then(r => r.ok ? r.json() : null)
        .then(u => {
          const placed = (u?.placements || []).length;
          if (placed) return `Can't delete — placed ${placed} time(s) in projects; remove those placements first.`;
          const used = (u?.components || []).length;
          if (used) return `Can't delete — used as a component in ${used} product(s); remove those references first.`;
          return null;
        })
        .catch(() => null);
    }
    const c = row.counts || {};
    if ((c.total || 0) > 0) return "Can't delete — empty it first";
    if ((row.count || 0) > 0) return "Can't delete — empty it first";
    return null;
  }, []);

  // Move (re-parent) — routes by row._type
  const handleMoveRow = useCallback(async (row, newParentId) => {
    if (!newParentId) return;
    const url = row._type === 'product'
      ? `/api/products/${row.id}/reparent`
      : `/api/products/categories/${row.id}/reparent`;
    const body = row._type === 'product'
      ? { category_id: newParentId }
      : { parent_id: newParentId };
    const res = await fetch(url, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    // THROWS: the buffered Save reads a quiet return as success, which would leave
    // the row looking saved and still sitting where it was (the exact trap found on
    // Catalog the same day).
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Move failed'); }
    reloadTree();
  }, [reloadTree]);

  // INF-8 — Promote to Standard (internal only): a project's own product becomes
  // everyone's. The server refuses while it uses project-only rows and names them;
  // the throw lands under the button that was pressed.
  const authGroup = useAuthGroup();
  // INF-14 — which rows this screen may change, decided in can-change.js. A
  // product's parameters and components are the product's: they lock with it.
  const { lockOwned, canArrange, folderLock } = useCanChange();
  const productLock = product ? lockOwned(product.owner_project_id, product.owner_project_code) : null;
  const handlePromoteProduct = useCallback(async (row) => {
    const res = await fetch(`/api/products/${row.id}/promote`, { method: 'POST' });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || 'Promote failed');
    setProduct(p => (p && String(p.id) === String(row.id) ? body : p));
    reloadTree();
  }, [reloadTree]);
  const onPromoteProduct = authGroup === 'internal' ? handlePromoteProduct : null;

  // §5 HEADER ROW (UI_VISION, merged 2026-07-20 — Alex: "definitively Merge.
  // We don't need both."). The PRODUCT identity section IS the product's own
  // row as it appears in the grid one level up: the SHARED folder grid's
  // product-leaf columns (Tag/Description/Status defined ONCE, status editor
  // via statusEditable) + the move cascade (PathDropdowns — the breadcrumb's
  // editable twin) + copy, then the product-only extras (Labour / Placeable /
  // Onshape ref) from makeProductCols.
  const headerRow = useMemo(() => {
    if (!product) return null;
    const path = product.category_id != null ? (findCatPath(`c3-${product.category_id}`) || []) : [];
    return {
      ...product,
      _uncharged_hours: unchargedHours,
      _ownerOnly: product.owner_project_code || null,
      _type: 'product',
      _l1Id: path[0]?._catId ?? null,
      _l2Id: path[1]?._catId ?? null,
      _l3Id: path[2]?._catId ?? null,
    };
  }, [product, findCatPath, unchargedHours]);

  // Move from the header row: reparent, then land on the product at its new
  // address — the node id embeds the category, so staying on the old id would
  // leave the tree/breadcrumb pointing at where it used to live.
  const handleHeaderMove = useCallback(async (row, newParentId) => {
    await handleMoveRow(row, newParentId);
    handleSelectNode(`c3-${newParentId}|${row.id}`);
  }, [handleMoveRow, handleSelectNode]);

  const productInfoCols = useMemo(() => makeFolderCols({
    seq: false,
    onPromote: onPromoteProduct,
    parentCol: {
      key: '_parent', label: 'Parent', width: 280,
      // The ORIGINAL parent, so DataGrid can tell a real change from re-picking the
      // shelf the row is already on. editValue rather than a field on the row: the
      // row shapes are built in three places and none of them needs to know this
      // column exists.
      editValue: row => row._l3Id ?? '',
      render: (row, ctx) => {
        const inEdit = editingProductInfoRowId != null && String(row.id) === String(editingProductInfoRowId);
        return (
          <PathDropdowns
            key={`${row.id}-hdr-${row._l1Id}-${row._l2Id}-${row._l3Id}`}
            row={row}
            categoryTree={categoryTree}
            depth={3}
            onPick={newParentId => ctx?.buffer?.(row.id, '_parent', newParentId)}
            disabled={!inEdit || !canArrange}
            onNavigate={nodeId => handleSelectNode(nodeId)}
          />
        );
      },
    },
    editable: true,
    items: null,
    statusMode: 'leaf',
    statusEditable: true,
    extraCols: [
      ...makeProductCols(laborLineOptions),
      {
        // db_id — item identity, so it lives in the item's row (one home;
        // moved here from the Onshape section with the breadcrumb retirement).
        key: '_dbid', label: 'db_id', width: 110,
        render: row => <DbIdBadge dbId={`prod:${row.id}`} />,
      },
    ],
  }), [laborLineOptions, categoryTree, editingProductInfoRowId, handleHeaderMove, handleSelectNode, onPromoteProduct, canArrange]);

  // Reorder within the current parent — routes by row._type
  const handleReorderHierarchy = useCallback(async (rowId, targetIndex) => {
    const row = catGridRows.find(r => String(r.id) === String(rowId));
    if (!row) return;
    const url = row._type === 'product'
      ? `/api/products/${rowId}/move`
      : `/api/products/categories/${rowId}/move`;
    const res = await fetch(url, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ toIndex: targetIndex }),
    });
    if (!res.ok) return;
    reloadTree();
  }, [catGridRows, reloadTree]);

  // Unified hierarchy grid columns — the SHARED folder grid (folder-cols.jsx;
  // UI_VISION §10 "easiest win", forced by §8.0). Screen-specifics ride in as
  // flags/slots: the PathDropdowns Parent column, Items = products beneath,
  // product rows get status badge + ⚠ error chip + ↗ (all in folder-cols.jsx).
  // Must live after handleMoveRow to avoid TDZ in the deps array.
  const hierarchyCols = useMemo(() => {
    const isCNode = !!(selectedNodeId && /^c[123]-\d+$/.test(selectedNodeId));
    const catLvl = isCNode ? parseInt(selectedNodeId[1], 10) : null;
    const isFamNode = !!(selectedNodeId && /^\d+$/.test(selectedNodeId));
    const isProductGrid = catLvl === 3 || isFamNode;
    const pathDepth = catLvl === 1 ? 1 : catLvl === 2 ? 2 : catLvl === 3 ? 3 : 0;

    // UX Consistency 2: ONE "Parent" column holding the full cascading PathDropdowns
    // at the row's path depth (was three "Move to" L1/L2/L3 columns). Matches the
    // single-column Catalog pattern. Read-only outside edit mode (shows the path).
    const parentCol = pathDepth >= 1
      ? {
          key: '_parent', label: 'Parent', width: pathDepth === 1 ? 130 : pathDepth === 2 ? 200 : 280,
          editValue: row => row[`_l${pathDepth}Id`] ?? '',
          render: (row, ctx) => {
            const inEdit = editingHierarchyRowId != null && String(row.id) === String(editingHierarchyRowId);
            return (
              <PathDropdowns
                key={`${row.id}-parent-${row._l1Id}-${row._l2Id}-${row._l3Id}`}
                row={row}
                categoryTree={categoryTree}
                depth={pathDepth}
                onPick={newParentId => ctx?.buffer?.(row.id, '_parent', newParentId)}
                disabled={!inEdit || !canArrange}
              />
            );
          },
        }
      : null;

    return makeFolderCols({
      seqKey: '_pos',
      parentCol,
      items: isProductGrid ? null : { value: row => (row.counts || {}).total || 0 },
      statusMode: isProductGrid ? 'leaf' : 'counts',
      onshapeCol: isProductGrid,
      onPromote: isProductGrid ? onPromoteProduct : null,
    });
  }, [selectedNodeId, categoryTree, handleMoveRow, editingHierarchyRowId, onPromoteProduct, canArrange]);

  const isCatNode = !!(selectedNodeId && /^c[123]-\d+$/.test(selectedNodeId));
  const isProductLevel = !!(selectedNodeId && selectedNodeId.includes('|'));
  // A numeric-only node id used to mean a FAMILY folder (e.g. "5" for CHW).
  // product_families is gone (2026-07-29) and the tree never emits such an id
  // now — but App persists selectedNodeId, so a stale one can still arrive from
  // localStorage. Kept as a constant false so those branches are dead rather
  // than reachable-with-no-data.
  const isFamilyNode = false;
  // Show root L1 grid: nothing selected, or a group label (l1-*), but NOT a family folder or product
  const showRootGrid = !selectedNodeId || (!isCatNode && !isProductLevel && !isFamilyNode);
  const catNodeLevel = isCatNode ? parseInt(selectedNodeId[1], 10) : null;

  // The hierarchy grid's card title — same branches as its own emptyMessage
  // below, so the two can never disagree about what's on screen (Alex,
  // 2026-08-20, matching the same fix on Catalog's own grid).
  const hierarchyCardTitle = catNodeLevel === 3 || isFamilyNode
    ? 'Products' : showRootGrid ? 'Categories' : 'Sub-categories';

  // --- Phase 3 (Alex, 2026-07-20: "go") — CATEGORY header rows -----------------
  // c1/c2/c3 category levels get their own header row, same pattern as the
  // product header row above — what lets the breadcrumb retire here entirely.
  // Unlike Catalog's L1 (a fixed cost-classification, cross-L1 reparent
  // disallowed by design), Products' categories are freely reparentable at any
  // depth — handleMoveRow + PathDropdowns already do this generically for the
  // CHILDREN grid, so the header row reuses both verbatim rather than hand-
  // rolling a second cascade.
  const [editingCatHeaderRowId, setEditingCatHeaderRowId] = useState(null);

  const categoryHeaderRow = useMemo(() => {
    if (!isCatNode) return null;
    const path = findCatPath(selectedNodeId) || [];
    const node = path[path.length - 1];
    if (!node) return null;
    // Items: L1/L2 = sub-categories beneath (what the grid below shows); L3 =
    // products beneath (the tree node's own children ARE the products there).
    const count = catNodeLevel === 3
      ? (node.children || []).filter(c => c.isProduct).length
      : (node.children || []).filter(c => !c.isProduct).length;
    return {
      id: node._catId, code: node.code, label: node.label, _type: 'category',
      count, counts: node.counts || {},
      _l1Id: path[0]?._catId ?? null,
      _l2Id: path[1]?._catId ?? null,
      // The assembly collecting this category's products (phase 4). Read off
      // the tree node so no extra fetch is needed.
      _assemblyRef: node.onshape_ref ?? null,
    };
  }, [isCatNode, selectedNodeId, findCatPath, catNodeLevel]);

  // A category's own id never changes on reparent (only its parent_id) — no
  // re-navigation needed, unlike the product header's move (whose node id
  // embeds its category).
  const handleCategoryHeaderMove = useCallback(async (row, newParentId) => {
    await handleMoveRow(row, newParentId);
    setEditingCatHeaderRowId(null);
  }, [handleMoveRow]);

  const categoryHeaderCols = useMemo(() => {
    // Parent cell depth = how many ancestor levels this row's OWN move needs:
    // L1 has no ancestor (nothing to show or move — no column at all); L2
    // needs one pick (a new L1); L3 needs two (a new L1 then L2).
    const depth = catNodeLevel === 2 ? 1 : catNodeLevel === 3 ? 2 : 0;
    const parentCol = depth >= 1 ? {
      key: '_parent', label: 'Parent', width: depth === 1 ? 130 : 200,
      editValue: row => row[`_l${depth}Id`] ?? '',
      render: (row, ctx) => {
        const inEdit = editingCatHeaderRowId != null && String(row.id) === String(editingCatHeaderRowId);
        return (
          <PathDropdowns
            key={`${row.id}-cathdr-${row._l1Id}-${row._l2Id}`}
            row={row} categoryTree={categoryTree} depth={depth}
            onPick={newParentId => ctx?.buffer?.(row.id, '_parent', newParentId)}
            disabled={!inEdit || !canArrange}
            onNavigate={nodeId => handleSelectNode(nodeId)}
          />
        );
      },
    } : null;
    return makeFolderCols({
      seq: false, parentCol, editable: true,
      items: { value: row => row.count },
      statusMode: 'counts',
    });
  }, [catNodeLevel, categoryTree, editingCatHeaderRowId, handleCategoryHeaderMove, handleSelectNode, canArrange]);

  // Legacy family-folder browsing (isFamilyNode, numeric ids like "5" for CHW)
  // — a DIFFERENT, older taxonomy than c1/c2/c3 (flatFamilies, not
  // categoryTree). Out of phase 3's scope (Alex asked for "L1/L2/L3 folders,
  // product categories"); kept to a minimal read-only identity line so the
  // breadcrumb still has nothing left to do, without building a second
  // header-row mechanism for a path being phased out.
  const familyFolderInfo = useMemo(() => {
    if (!isFamilyNode) return null;
    return null;   // product_families is gone; nothing to look up
  }, [isFamilyNode, selectedNodeId]);

  const isAtTypical = !product || product.params.every(
    p => (localValues[p.name] ?? p.typical_value) === p.typical_value
  );

  // Footer total row — must be after isAtTypical and isProductLevel are defined.
  const compFooterRow = useMemo(() => {
    if (!isProductLevel || loading || !gridRows.length) return null;
    const totalMat = Math.round(gridRows.reduce((s, r) => s + (r.mat_cost ?? 0), 0) * 100) / 100;
    const totalLabor = Math.round(gridRows.reduce((s, r) => s + (r.labor_cost ?? 0), 0) * 100) / 100;
    const label = isAtTypical ? 'Typical total' : 'Preview total';
    const labelColor = isAtTypical ? NAVY : 'var(--th-warn-accent)';
    // One size across the four totals (Alex, 2026-08-21: "use the same formats
    // as hours"): every total takes its cell's DEFAULT size (var(--fs-small), the
    // same as the body rows and CatalogView's Project total). No per-cell
    // fontSize: an override on hours alone is what made 18,722 render larger
    // than the money beside it (Alex, 2026-10-12).
    const $b = v => v > 0 ? <MoneyCell n={v} bold /> : null;
    return {
      description: <strong style={{ color: labelColor, fontSize: 'var(--fs-small)' }}>{label}</strong>,
      mat_cost: $b(totalMat),
      hours: totalHours != null ? <HourCell n={totalHours} bold /> : null,
      labor_cost: $b(totalLabor),
      line_cost: totalCost != null ? <MoneyCell n={totalCost} bold /> : null,
    };
  }, [isProductLevel, loading, gridRows, isAtTypical, totalCost, totalHours]);

  // ── WHAT THE ASSISTANT WROTE HERE (2026-10-13) ────────────────────────────────────────
  // The chat may fill this product's header and parameters in one batch (assistant-writes.js).
  // The shared notice names it above the parameters, the rows are shaded, Undo all reverts it.
  const productId = isProductLevel ? product?.id ?? null : null;
  const reloadProduct = useCallback(() => {
    if (!productId) return;
    fetch(`/api/products/${productId}`)
      .then(r => (r.ok ? r.json() : null))
      .then(p => { if (p) setProduct(p); });
    reloadTree();
  }, [productId, reloadTree]);
  const assistantWrite = useAssistantWrite({ kind: 'product', id: productId, reload: reloadProduct, refreshKey: product });
  const assistantChangedParams = useMemo(
    () => new Set((assistantWrite.write?.changes?.params || []).map(p => p.id)),
    [assistantWrite.write],
  );

  // See CatalogView: when the Onshape screen embeds this one, the assistant belongs to the
  // HOST workspace, not this one. Two of them side by side is what Alex hit on 2026-08-16.
  // `screen` scopes the chat's one write tool to the product open here.
  const assistant = (
    <AssistantSection
      screen={productId ? { kind: 'product', id: productId, label: [product.code, product.label].filter(Boolean).join(' ') } : null}
    />
  );

  // ── THE MIDDLE'S SPOT STACK (Alex's parking model, 2026-08-16) ────────────────────────
  // The sections below the level's own content, in order. The shell owns each one's title
  // bar, collapse and remembering; this array owns only WHAT and IN WHAT ORDER.
  //
  // ⚠ A spot with no `title` renders bare, and three of these four are bare on purpose:
  //   • Configuration — its title lives INSIDE a `width: max-content` wrapper so the
  //     "Reset to typical" button lines up with the Simulate COLUMN rather than the
  //     container edge (measured 2026-08-02: 949 vs 977, the button was 29px out). Hoisting
  //     the title into the shell would align it to the container and break that. It becomes
  //     a titled spot when that alignment is solved, not before.
  //   • Sync worklist / Where used — shared components that already draw their own
  //     collapsible frame on other surfaces; a shell title would give them two.
  // Components is the one that converts cleanly, and it is the proof: count and the
  // "show mappings" toggle go through the shell's own title bar.
  const productSpots = [
    // HOURS BUT NO LABOUR — first spot, straight under the Product row (Alex,
    // 2026-10-14: move it up, to the standard warning place). Same place the
    // assistant's write notice uses: what the user must act on on THIS product.
    isProductLevel && product && unchargedHours > 0 && {
      key: 'labour-warning',
      content: (
        <Notice kind="warning">
          {product._labor_label
            ? <>{formatQty(unchargedHours)} h of install hours are not charged: the labour line <b>{product._labor_label}</b> has no rate. Give it a price in the Catalog, or pick another Labour line in the Product row above.</>
            : <>{formatQty(unchargedHours)} h of install hours are not charged: this product has no Labour line, and its category gives none. Pick one in the Labour column of the Product row above.</>}
        </Notice>
      ),
    },
    isProductLevel && product && assistantWrite.write && {
      key: 'assistant-write',
      content: (
        <AssistantWriteNotice
          write={assistantWrite.write}
          groups={[
            { title: 'Product', items: assistantWrite.write.changes.product.length ? [{ name: product.label, fields: assistantWrite.write.changes.product }] : [] },
            { title: 'Parameters (shaded below)', items: assistantWrite.write.changes.params },
          ]}
          error={assistantWrite.error}
          onUndo={assistantWrite.undo}
          onDismiss={assistantWrite.dismiss}
        />
      ),
    },
    isProductLevel && product && {
      key: 'onshape',
      // ⚠ THE SHELL DRAWS THIS TITLE, and OnshapeSection needed no new flag to allow it —
      // `title=''` already suppressed its own row, for callers that supply a heading above
      // it. So the shared component keeps its frame on the five surfaces that want one and
      // hands it over here, which is the "flags, not forks" rule doing its job.
      // ⚠ One behaviour change worth knowing: this section's own collapse key
      // (inoviu:section:onshape:product) stops being read on THIS screen, so a 3D Model
      // block left collapsed here reopens once, then remembers under the shell's key.
      title: '3D Model',
      card: true,
      // The product's picture beside the 3D MODEL title (Alex, 2026-08-27).
      // ⚠ It goes on the SPOT, not on OnshapeSection: this surface passes
      // title='' so the section suppresses its own title row and the SHELL
      // draws it (see the note above), which means the section's own icon
      // would render nowhere.
      icon: <ImageCell dbId={`prod:${product.id}`} onshapeRef={product.onshape_ref || null} size={20} />,
      content: (
        // The SHARED block (components/OnshapeSection.jsx, UI_VISION §8 step 1). Thumbnail /
        // link row / View 3D / Download IFC live in the section; this surface contributes
        // the db_id (the value to paste into an Onshape part when the part IS this product)
        // and the component sync. "Sync from Onshape" (massprops → config params) stays
        // removed (2026-07-17, Alex: "it added stuff in the configuration instead") — the
        // component sync below is the ONE Onshape sync.
        // ADD WHEN EMPTY (UI_VISION §0, phase 2): the section renders even unlinked, so the
        // link editor is always reachable; the SYNC appears only once there is a ref.
        <OnshapeSection
          onshapeRef={product.onshape_ref || null}
          storageKey="product"
          title=""
          frame={false}
          downloadName={[product.code, product.label].filter(Boolean).join(' ')}
        >
          <ProductOnshapeLink
            productId={product.id}
            onshapeRef={product.onshape_ref}
            onSaved={() => fetch(`/api/products/${product.id}`).then(r => r.ok ? r.json() : null).then(p => { if (p) setProduct(p); })}
          />
          {/* ON-21 — ONE BUTTON, ONE LIST, ONE OK. This replaced the two panels
              that used to stand here: CompsSyncPanel (Preview → Apply) and
              ConfigCheckPanel (Check → OK each finding). Same two reads, same two
              stores, same writes — one review instead of two, and one OK that
              writes exactly what is ticked. Both panels still exist and are still
              used by the Onshape SIDE PANEL block until ON-21 step 4 mounts this
              flow there too. */}
          {product.onshape_ref && (
            <OnshapeSyncReview
              productId={product.id}
              onshapeRef={product.onshape_ref}
              syncedAt={product.onshape_synced_at}
              refreshToken={worklistToken}
              onApplied={() => fetch(`/api/products/${product.id}`).then(r => r.ok ? r.json() : null).then(p => { if (p) setProduct(p); })}
              onRead={() => setWorklistToken(t => t + 1)}
            />
          )}
        </OnshapeSection>
      ),
    },
    isProductLevel && product && {
      key: 'configuration',
      card: true,
      // Title moved to the shell (2026-08-20, same pass that gave it a chevron) —
      // it used to be drawn inline, in the same row as the Reset button, because
      // that row needed the SAME width:max-content wrapper as the grid below (see
      // that wrapper's own comment). Splitting them out doesn't touch the
      // alignment trick: the wrapper still measures itself off the grid, the
      // button row inside it just no longer shares that row with a title.
      title: 'Configuration',
      count: `${product?.params?.length || 0} param${product?.params?.length !== 1 ? 's' : ''}`,
      content: (
        <div style={{ marginBottom: 14 }}>
          {/* width:max-content shrinks this wrapper to the TABLE's width, which is the
              sum of its column widths and is usually narrower than the container (949
              vs 977 when measured). Right-aligning against the container instead put
              the button 29px past the Simulate column. Because it tracks the table,
              it also survives a column resize. */}
          <div style={{ width: 'max-content', maxWidth: '100%' }}>
          {/* The simulate controls sit at the far RIGHT, over the Simulate column
              they belong to (Alex, 2026-08-02) — the button is PARAM_SIMULATE_W
              wide, the same width as that column, so the two line up rather than
              merely both being right-ish. Only offered while something is
              actually being simulated — a Reset that is always lit invites a
              click that does nothing. */}
            {simulating && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-warn-accent)', fontWeight: 700, textAlign: 'right' }}>
                  simulating — costs below are a preview, nothing is saved
                </span>
                <button
                  onClick={handleReset}
                  style={{ ...BTN_SECONDARY, width: PARAM_SIMULATE_W, flexShrink: 0 }}
                >Reset to typical</button>
              </div>
            )}
          <DataGrid
            // Keyed to the product so navigating to another one CLOSES an open add
            // row and drops its draft -- the job the retired setAddParamOpen(false)
            // used to do in handleSelectNode. DataGrid owns that open/closed state
            // now, so the consumer can only reset it by remounting.
            key={product?.id ?? 'none'}
            rows={product?.params || []}
            columns={paramCols}
            onSave={handleParamCellSave}
            onDelete={handleDeleteParam}
            onDeleteCheck={handleParamDeleteCheck}
            onCopy={handleCopyParam}
            onReorder={canArrange && !productLock ? handleReorderParam : undefined}
            rowLocked={() => productLock}
            onEditRow={setEditingParamRowId}
            editRowId={editingParamRowId}
            editOnRowClick
            getRowStyle={(row) => (assistantChangedParams.has(row.id) ? { background: 'var(--th-highlight-bg)' } : null)}
            emptyMessage="No parameters defined. Add one below."
            storageKey="product-params"
            // ADD IS NOW IN THE GRID (Alex, twice: "the UI is not consistent with the
            // other: Fields are not in line with columns. Save Cancel are not on the
            // bottom left" / "Add parameter is not consistent with the parameter data
            // grid row"). It was a wrapped flex box of labelled fields UNDER the table,
            // so nothing lined up with the column it was filling and the field order
            // did not even match the header order. Same S3e in-cell lane the COMPONENTS
            // grid already uses: one editor per column, in that column, with Add/Cancel
            // bottom-left in the sub-row.
            {...(productLock ? {} : {
              addFormLabel: 'Add parameter',
              addInlineEditRender: paramAddInlineEditRender,
              renderAddSubRow: renderParamAddSubRow,
              onAddOpenChange: open => { if (open) { setNewParam(NEW_PARAM_INIT); setNewOptionDraft({ value: '', label: '' }); setEditingParamRowId(null); } },
            })}
          />
          </div>
        </div>
      ),
    },
    // Gated on isProductLevel && product, same as every other product-only spot
    // (Alex, 2026-08-20: this used to render at every category/root level too,
    // "meaningful at category level" — traced it and found nothing wires a
    // category-grid row click to it; `liveRows` returns `[] ` whenever `product`
    // is null (ProductsView.jsx ~1966), so it was a permanently-empty card,
    // reachable but never populated. Confirmed dead, removed rather than kept
    // as furniture — a real preview-on-row-click feature would be new work, not
    // a restore).
    isProductLevel && product && {
      key: 'components',
      // The shell draws this title — count and the toggle ride through it, which is the
      // whole point: the block contributes content and the frame is not its business.
      title: 'Components',
      card: true,
      count: componentsCountLabel(gridRows.length, notSyncedCount),
      // The "show mappings" toggle that lived here is GONE (2026-08-21): the
      // mapped values now render on every param_map row by default, because they
      // are what tells two otherwise-identical rows apart.
      right: null,
      content: (
        // Components — S3d/S3e: in-cell editing of the selected row AND in-cell adding
        // (the "+ Add component" ghost row opens the same editors in a blank row) + a
        // parameters extension row. ComponentsGrid owns both shared editors.
        <ComponentsGrid
          product={product}
          gridRows={gridRows}
          gridColumns={gridColumns}
          categoryTree={categoryTree}
          catalogTaxonomy={catalogTaxonomy}
          localValues={localValues}
          searchQuery={searchQuery}
          searchResults={searchResults}
          onCompSearch={handleCompSearch}
          onCompFormSave={handleCompFormSave}
          onDeleteComp={handleDeleteComp}
          onCopyComp={handleCopyComp}
          onReorderComp={canArrange ? handleReorderComp : undefined}
          compFooterRow={compFooterRow}
          selectedCompId={selectedCompId}
          setSelectedCompId={setSelectedCompId}
          loading={loading}
          emptyMessage="No components defined."
          isProductLevel={isProductLevel}
          locked={productLock}
          addFormLabel="Add component"
          sizeListValuesCache={sizeListValuesCache}
          fetchSizeListValues={fetchSizeListValues}
        />
      ),
    },
    // ⚠ Gated on onshape_ref HERE, not just left to SyncWorklistSection's own
    // `if (!onshapeRef) return null` — Alex, 2026-08-20, found "a loose rectangle
    // between Where Used and Components" on a product with no ref. Spot cannot see
    // THROUGH an unevaluated `<SyncWorklistSection/>` element to know it renders
    // null; `spot.content` is always a truthy React element regardless of what
    // that component eventually outputs, so the card wrapper drew its border
    // around nothing. The card must not exist for a product this doesn't apply to
    // — the same reason every other product-only spot is gated at this level.
    isProductLevel && product && product.onshape_ref && {
      key: 'sync-worklist',
      // Sync worklist — the negative space of the grid above: the parts the last preview
      // would NOT write, retained until a preview stops reporting them. Read-only by
      // design (there is no dismiss).
      card: true,
      content: (
        <SyncWorklistSection
          productId={product.id}
          onshapeRef={product.onshape_ref || null}
          refreshToken={worklistToken}
          onPromoted={() => fetch(`/api/products/${product.id}`).then(r => r.ok ? r.json() : null).then(p => { if (p) setProduct(p); })}
          topRule={false}
        />
      ),
    },
    // The third place a synced part lands (2026-10-11): parts the model itself
    // marks as not priced. Same gate as Not synced, for the same reason.
    isProductLevel && product && product.onshape_ref && {
      key: 'sync-not-applicable',
      card: true,
      content: (
        <SyncNotApplicableSection
          productId={product.id}
          onshapeRef={product.onshape_ref || null}
          refreshToken={worklistToken}
          topRule={false}
        />
      ),
    },
    // Where used — TWO independent cards (Alex, 2026-08-20), not one section with
    // two nested sub-collapses. Both read the same `usage` fetch; see
    // usageCardContent's own header comment for why that stays shared.
    isProductLevel && product && {
      key: 'used-in-products',
      card: true,
      defaultOpen: false,
      onOpen: () => { if (usage == null) loadUsage(); },
      title: 'Used in Products',
      count: usage != null ? usage.components?.length ?? 0 : undefined,
      content: usageCardContent(
        usage,
        (usage?.components ?? []).map(c => ({ ...c, id: c.product_id })),
        USED_IN_PRODUCTS_COLS,
        onNavigateToProduct ? (row) => onNavigateToProduct(row.product_id) : undefined,
        'Not used as a component in any product.',
        'product-usage-products',
      ),
    },
    isProductLevel && product && {
      key: 'placed-in-projects',
      card: true,
      defaultOpen: false,
      onOpen: () => { if (usage == null) loadUsage(); },
      title: 'Placed in Projects',
      count: usage != null ? usage.placements?.length ?? 0 : undefined,
      content: usageCardContent(
        usage,
        (usage?.placements ?? []).map(p => ({ ...p, id: p.placement_id })),
        PLACED_IN_PROJECTS_COLS,
        onNavigateToPlacement ? (row) => onNavigateToPlacement(row) : undefined,
        'Not placed in any project.',
        'product-usage-placements',
      ),
    },
  ];

  const shownCatGridRows = shownIds ? catGridRows.filter(r => shownIds.has(r.nodeId)) : catGridRows;

  return (
    <HierarchyWorkspace
      rightBottom={assistant}
      spots={productSpots}
      treeNodes={shownTree}
      selectedNodeId={selectedNodeId}
      onSelectNode={handleSelectNode}
      treeHeader={<ViewSwitcher view={view} setView={setView} onSelectRoot={() => handleSelectNode(null)} />}
      treeFilterAside={workingIn ? <ShowStandardCheck workingCode={workingCode} checked={showStandard} onChange={setShowStandard} /> : null}
      treeEmptyMessage={projectOnly && !shownTree.length ? nothingOwnMessage(workingCode) : undefined}
      filterPlaceholder="Filter products…"
      hideNav={hideNav}
      storageKey="products"
      defaultNavWidth={240}
      // Pinned header + one shared scroll below it (Alex, 2026-08-21: "is it
      // possible to freeze the header card?"… "Same for all for the middle
      // section. Typical") — the same opt-in CatalogView uses, now a shared
      // HierarchyWorkspace behavior rather than a second hand-rolled copy.
      mainStyle={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: `${PANE_GAP}px ${PANE_GAP}px 0` }}
      mainHeader={
        // BREADCRUMB RETIRED entirely (Alex, 2026-07-20: phases 1+2 did the
        // product page, phase 3 — "go" — does category levels). Root has no
        // node to show a row for, so it keeps the hint text; every other
        // selection (category or product) now has a header row of its own.
        //
        // isProductLevel — the product's own §5 row (Tag/Label/Status/…)
        // PINS here (2026-08-21) exactly like Catalog's category row; its 3D
        // Model/Configuration/Components sections stay in `children`, which
        // is what now scrolls independently beneath it.
        isProductLevel ? (headerRow ? (
          <HeaderRow
            title="Product"
            row={headerRow}
            // The product's picture beside its own title (Alex, 2026-08-27).
            dbId={`prod:${headerRow.id}`}
            onshapeRef={headerRow.onshape_ref}
            columns={productInfoCols}
            onSave={(rowId, field, value) => (field === '_parent'
              // Same branch that used to run on pick; only WHEN it runs changed.
              // handleHeaderMove also lands the view on the product's new address,
              // because the node id embeds the category it lives under.
              ? handleHeaderMove({ id: rowId, _type: 'product' }, Number(value))
              : handleProductCellSave(rowId, field, value))}
            onCopy={handleCopyHierarchyRow}
            rowLocked={() => productLock}
            onEditRow={setEditingProductInfoRowId}
            editRowId={editingProductInfoRowId}
            storageKey="product-info"
            card
          />
        ) : undefined) : categoryHeaderRow ? (
          <>
            <HeaderRow
              title="Category"
              row={categoryHeaderRow}
              columns={categoryHeaderCols}
              onSave={(rowId, field, value) => (field === '_parent'
                ? handleCategoryHeaderMove({ id: rowId, _type: 'category' }, Number(value))
                : handleSaveCatLabel(rowId, field, value))}
              onEditRow={setEditingCatHeaderRowId}
              rowLocked={() => folderLock}
              editRowId={editingCatHeaderRowId}
              storageKey="products-category-header"
              card
            />
            {/* The category's Onshape ASSEMBLY (phase 4) — below its header row,
                the same order the catalog side uses. ALWAYS applicable: unlike
                catalog HR (labour, which can never be modelled), Alex's rule for
                products is that any of them could gain a model eventually —
                "from a small product to the biggest product, we always have an
                opportunity to have Onshape. At least one day or another." So the
                product side has no cost_class-style gate at all. */}
            <CategoryOnshapePanel
              categoryId={categoryHeaderRow.id}
              onshapeAssemblyRef={categoryHeaderRow._assemblyRef || ''}
              patchUrl={`/api/products/categories/${categoryHeaderRow.id}`}
              storageKey="products-category"
              onSaved={reloadTree}
            />
          </>
        ) : familyFolderInfo ? (
          // Legacy family folder — read-only identity line (see the
          // familyFolderInfo definition above for why this stays minimal).
          <div style={{ marginBottom: 10, flexShrink: 0 }}>
            <SectionTitle>Family</SectionTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 22 }}>
              <span style={{ fontFamily: 'var(--ff-mono)', fontWeight: 700, color: 'var(--th-navy)', fontSize: 'var(--fs-body)' }}>{familyFolderInfo.code}</span>
              <span style={{ fontSize: 'var(--fs-body)', color: 'var(--th-text-body)' }}>{familyFolderInfo.label}</span>
            </div>
          </div>
        ) : (
          <Card storageKey="products:section:no-selection" title="Product">
            <span style={{ color: 'var(--th-text-hint)', fontSize: 'var(--fs-body)' }}>Select a product in the navigation tree.</span>
          </Card>
        )
      }
      rightPanel={
        /* The "Configuration — Definition" panel was REMOVED 2026-08-02. Alex never
           used it — he edits the parameters grid directly — and its one unique job
           (preview several parameters, then commit them all as typical) is now the
           grid's own Simulate column, which cannot destroy a typical by accident.
           Its slot is what the Notes card (DS-4, 2026-08-21) now fills — same
           card CatalogView's own right panel got first, here reading/writing
           products.notes instead of a category/family's. */
        <NotesSection
          key={product ? product.id : 'none'}
          notes={product?.notes}
          itemLabel={product ? product.label : 'nothing selected'}
          onSave={async (text) => {
            const res = await fetch(`/api/products/${product.id}`, {
              method: 'PATCH', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ notes: text }),
            });
            if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Save failed'); }
            const fresh = await fetch(`/api/products/${product.id}`).then(r => r.json());
            setProduct(fresh);
          }}
          storageKey="products:section:notes"
          applicable={!!product}
          naReason="Select a product to see its notes."
        />
      }
    >
        {/* ── Hierarchy grid (ProductHierarchyGrid BUILD 1+2, 1b-refinements) ── */}
        {/* key=selectedNodeId forces a clean remount on every node switch (Fix 2) */}
        {(isCatNode || showRootGrid || isFamilyNode) && (
          <Card storageKey="products:section:grid" title={hierarchyCardTitle} count={shownCatGridRows.length}>
            <DataGrid
              key={selectedNodeId || 'root'}
              rows={shownCatGridRows}
              columns={hierarchyCols}
              onRowClick={row => setSelectedHierarchyRowId(row.id === selectedHierarchyRowId ? null : row.id)}
              onRowDoubleClick={row => { setSelectedHierarchyRowId(null); handleSelectNode(row.nodeId); }}
              selectedRowId={selectedHierarchyRowId}
              onSave={!isFamilyNode
                ? ((rowId, field, value) => {
                  if (field !== '_parent') return handleSaveHierarchyRow(rowId, field, value);
                  // handleMoveRow routes by row TYPE, which is what keeps a product
                  // reparent and a category reparent on their own endpoints.
                  const r = catGridRows.find(x => String(x.id) === String(rowId));
                  return r ? handleMoveRow(r, Number(value)) : undefined;
                })
                : undefined}
              onAdd={
                isFamilyNode ? handleAddProductToFamily :
                showRootGrid ? (canArrange ? handleAddL1Category : undefined) :
                catNodeLevel === 3 ? handleAddProductRow :
                canArrange ? handleAddCategory : undefined
              }
              onDelete={isFamilyNode ? undefined : handleDeleteHierarchyRow}
              onCopy={catNodeLevel === 3 || isFamilyNode ? handleCopyHierarchyRow : undefined}
              onDeleteCheck={isFamilyNode ? undefined : handleDeleteCheck}
              onReorder={canArrange ? handleReorderHierarchy : undefined}
              rowLocked={r => (r._type === 'product' ? lockOwned(r.owner_project_id, r._ownerOnly) : folderLock)}
              onEditRow={id => setEditingHierarchyRowId(id)}
              editRowId={editingHierarchyRowId}
              // Auto-edit (2026-08-22, Alex: "switch everything to auto edit") — this
              // grid has no renderSubRow tied to selectedHierarchyRowId, and navigation
              // is on double-click (handleSelectNode), so single click is free to become
              // the edit trigger with nothing else lost.
              editOnRowClick
              emptyMessage={
                isFamilyNode ? 'No products in this family.' :
                catNodeLevel === 3 ? 'No products in this category.' :
                showRootGrid ? 'No product categories yet. Add one to get started.' :
                'No sub-categories yet. Add one below.'
              }
              storageKey={catNodeLevel === 3 || isFamilyNode ? 'prod-items' : 'prod-cats'}
              multiSelect={catNodeLevel === 3 || isFamilyNode}
            />
          </Card>
        )}

        {/* ERR-1 — action failures (save, delete, copy, add component, param
            validation) appear HERE, above the product's own row, instead of in a
            browser popup that hid the thing being edited. */}
        <Notice onDismiss={() => setActionError(null)}>{actionError}</Notice>

        {/* ── Product create form (BUILD 1) ── */}
        {productFormMode === 'create' && (
          <div style={{ marginBottom: 12, padding: '8px 12px', border: '1px solid var(--th-card-border)', borderRadius: 4, background: 'var(--th-card-bg)' }}>
            <div style={{ fontSize: 'var(--fs-small)', fontWeight: 700, color: 'var(--th-text-body)', marginBottom: 8 }}>
              {productFormMode === 'create' ? 'New Product' : 'Edit Product'}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {(['code', 'label'] ).map(key => (
                <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>
                  {key === 'code' ? 'Tag (RDS Code)' : 'Label'}
                  <input
                    value={productFormFields[key]}
                    onChange={e => setProductFormFields(f => ({ ...f, [key]: e.target.value }))}
                    style={{ fontSize: 'var(--fs-small)', padding: '3px 6px', border: '1px solid var(--th-border-subtle)', borderRadius: 3, width: key === 'label' ? 200 : 100 }}
                  />
                </label>
              ))}
              {productFormFields.category_id ? (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>
                  Category
                  <span style={{ fontSize: 'var(--fs-small)', padding: '3px 6px', background: 'var(--th-chip-alt-bg)', borderRadius: 3, color: 'var(--th-navy)' }}>
                    {(() => { const p = findCatPath(`c3-${productFormFields.category_id}`); return p ? p.map(n => n.label).join(` ${CRUMB_SEP} `) : `L3 #${productFormFields.category_id}`; })()}
                  </span>
                </label>
              ) : (
                // The Family dropdown went with product_families (2026-07-29).
                // A product belongs to an L3 category and nothing else, so with
                // no category picked there is nothing to choose — say so rather
                // than offer a control that cannot be satisfied.
                <Text kind="deprecated">
                  Select an L3 category in the tree first.
                </Text>
              )}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>
                Status
                <select
                  value={productFormFields.status}
                  onChange={e => setProductFormFields(f => ({ ...f, status: e.target.value }))}
                  style={{ fontSize: 'var(--fs-small)', padding: '3px 6px', border: '1px solid var(--th-border-subtle)', borderRadius: 3 }}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="deprecated">Deprecated</option>
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>
                Trade
                <select
                  value={productFormFields.trade}
                  onChange={e => setProductFormFields(f => ({ ...f, trade: e.target.value }))}
                  style={{ fontSize: 'var(--fs-small)', padding: '3px 6px', border: '1px solid var(--th-border-subtle)', borderRadius: 3 }}
                >
                  <option value="">— none —</option>
                  {tradeOptions.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)', paddingBottom: 2 }}>
                <input
                  type="checkbox"
                  checked={!!productFormFields.placeable}
                  onChange={e => setProductFormFields(f => ({ ...f, placeable: e.target.checked }))}
                />
                Placeable
              </label>
              <button
                onClick={handleSaveProduct}
                disabled={savingProduct}
                style={{ ...BTN_PRIMARY, ...(savingProduct ? BTN_DISABLED : null) }}
              >{savingProduct ? 'Saving…' : 'Save'}</button>
              <button onClick={() => setProductFormMode(null)} style={BTN_SECONDARY}>Cancel</button>
            </div>
          </div>
        )}

    </HierarchyWorkspace>
  );
}
